import { api } from './api';
import type { Lead } from '../types';
import type { LeadFilter } from './leadFilterApi';

/** Full SimilarWeb marketing-mix bundle from the TIER-2 radeance scrape (only present when
 *  similarweb_overview_at is set). traffic_sources = 10-channel mix as fractions of 1:
 *  direct / search_organic / search_paid / social_organic / social_paid / referrals / mail /
 *  display / affiliate / gen_ai. gen_ai_by_llm = per-LLM share (chatgpt/claude/gemini/…). */
export interface SimilarwebOverview {
  visits?: number;
  traffic_sources?: Record<string, number>;
  gen_ai_by_llm?: Record<string, number>;
  bounce_rate?: number;
  pages_per_visit?: number;
  time_on_site?: number;
  global_rank?: number;
  category_rank?: number;
  top_country?: string;
  top_country_shares?: Record<string, number> | unknown;
  top_keywords?: unknown[];
  category?: string;
}


// Ein Eintrag im Sende-Verlauf eines Leads: welche Nachricht (Vorlage) wann von wem rausging.
// Seit 2026-07-30 liegen ZWEI Kanäle im selben Log; wer liest, MUSS filtern (lib/msgLog.ts):
// 'pitch'/'followup' = LinkedIn, 'mail'/'mail_followup' = E-Mail. Ohne Filter zeigt der
// LinkedIn-Verlauf verschickte Mails als „3. Nachfassen".
export type MsgLogKind = 'pitch' | 'followup' | 'mail' | 'mail_followup';

export interface MsgLogEntry {
  kind: MsgLogKind;
  step: number;              // 0 = Anschreiben/Erst-Mail, 1..n = Nachfass-Runde
  tpl: string | null;        // Vorlagen-ID ('standard' | 'sea_*' | 'kalt' | 'own:<id>')
  tpl_label: string | null;  // Klartext-Name der Vorlage zum Sendezeitpunkt
  at: string;
  by: number | null;
  by_name: string | null;
  body?: string | null;      // der tatsächlich abgesendete Text
  subject?: string | null;   // nur bei den Mail-Kinds
  // FORM der Nachricht, nicht ihr Kanal. 'memo' = auf LinkedIn ging eine Sprachnachricht raus,
  // `body` ist dann das Transkript und `tpl`/`tpl_label` sind leer (eine Memo hat keine Vorlage).
  // Fehlt/null = getippter Text — so sind alle Einträge von vor 2026-08-06 zu lesen.
  fmt?: 'memo' | null;
}

// Warum eine Karte gerade gesperrt ist. Früher liess jede dieser Regeln den Lead aus dem Stapel
// VERSCHWINDEN (und damit aus der Suche); jetzt bleibt die Karte, sagt den Grund und rutscht ans Ende.
// Spiegel der Server-Schlüssel in db_web/lead_gen.py (_build_lead_where → _gate).
export type BlockReason =
  | 'do_not_contact'      // Firma dauerhaft gesperrt („nicht anrufen") — läuft nicht ab, wird aufgehoben
  | 'followup_pending'    // Nachfass-Frist der aktuellen Runde läuft noch
  | 'callback_scheduled'  // Rückruf ist für später verabredet
  | 'cooldown'            // Nachfass-Runden ausgeschöpft → Abkühlphase, kommt danach zurück
  | 'reserved_other'      // heute für jemand anderen reserviert
  | 'locked_other'        // jemand anderes hat den Lead in Bearbeitung (Soft-Sperre)
  | 'viewing_other';      // jemand anderes hat die Karte gerade offen (Live-Präsenz)

export interface DomainLead {
  id: number;
  domain: string;
  client_id: number | null;
  client_name: string | null;
  tranco_rank: number | null;
  crux_rank_magnitude: number | null;   // CrUX size bucket: 1000/10000/100000/1000000
  similarweb_visits: number | null;     // manual: official SimilarWeb monthly visits (hard ICP gate)
  similarweb_updated_at: string | null; // when the SimilarWeb figure was last entered (auto-stamped)
  similarweb_overview: SimilarwebOverview | null; // Tier-2 radeance marketing-mix bundle (10 channels)
  similarweb_overview_at: string | null;          // when the radeance mix was fetched (gates real-vs-junk)
  status: string;
  // S5: die zuletzt erfasste Situation (Chip bei "Antwort erhalten" / "Aufgeben" auf der
  // Wisch-Karte, s. `lib/situations.ts`) — server-berechnet (`db_web/lead_gen.py` get_domain_leads,
  // korrelierte Subquery über `outreach_conversations`), write-only bis hierher: die Chips
  // schreiben schon lange, niemand las bislang zurück.
  latest_situation: string | null;
  // Sales-CRM-Rückmeldung: wer den Lead im Sales CRM angerufen hat ("Mani hat angerufen")
  called_by: string | null;          // Vorname des Callers
  called_by_user_id: number | null;
  called_at: string | null;          // Zeitpunkt des letzten Anrufs
  call_count: number | null;         // Anzahl Anrufe aus dem Sales CRM
  // ads.txt
  adstxt_present: number | null;
  adstxt_total_entries: number | null;
  adstxt_reseller_entries: number | null;
  adstxt_open_exchange_exposed: number | null;
  adstxt_avg_fraud_rate: number | null;
  adstxt_estimated_monthly_fraud_loss: number | null;
  // ad-tech / fraud-protection
  adtech_stack_json: string[] | null;
  fraud_protection_present: number | null;
  fraud_protection_vendors_json: string[] | null;
  runs_google_ads: number | null;   // 1 = paid Google-Ads tags detected, 0 = none, null = unchecked
  // advertiser-signal bundle (homepage + GTM-container deep scan)
  google_ads_conversion_ids: string[] | null; // captured AW-XXXX conversion ids (the pitch fact)
  has_meta_pixel: number | null;
  meta_pixel_ids: string[] | null;
  has_gtm: number | null;
  gtm_container_ids: string[] | null;
  conversion_events: string[] | null;         // purchase / begin_checkout / add_to_cart / …
  advertiser_score: number | null;            // 0-100 advertiser-ICP fit (successor to fraud_score)
  // meta
  active_meta_campaigns: number | null;
  // dataforseo
  seo_organic_traffic: number | null;
  seo_paid_traffic: number | null;
  seo_keyword_count: number | null;
  seo_no_data: number | null;
  // crux (ground-truth traffic presence)
  crux_present: number | null;   // 1 = verified real traffic, 0 = below floor, null = unchecked
  crux_origin: string | null;
  crux_lcp_p75: number | null;
  crux_inp_p75: number | null;
  crux_cls_p75: number | null;
  country: string | null;     // ISO cc (de/at/ch/us/…) · drives the per-user country preference
  // business classification (AI vertical + advertiser/publisher)
  business_category: string | null;
  // Feinkategorie, aus similarweb_overview.category ausgepackt (past-plans Stufe 1). NULL = nie
  // geholt, nicht "keine Kategorie". sw_category_group ist der Oberzweig (Abschnitt vor '/').
  sw_category: string | null;
  sw_category_group: string | null;
  // Gebatcht angehängt (past-plans Stufe 3, db_web/lead_gen.py:_attach_top_competitor) — keine
  // eigene Spalte auf domain_leads. Der stärkste, NICHT bereits angeschriebene Wettbewerber
  // (Default-Schalter verbirgt contacted/won-Rivalen). undefined/null = keine Kante über der
  // Schwelle, nicht "kein Wettbewerber existiert".
  competitor_json?: {
    rival_id: number | null;
    rival_domain: string;
    score: number;
    achsen: { kw?: number | null; cat?: number | null; size?: number | null; land?: number | null; shop?: number | null };
    grund: string;
    // Anonyme Kennzahlen, ohne Namens-Gate — leer (null) bei >180 Tagen Altersspreizung
    // zwischen den beiden SimilarWeb-Abzügen (past-plans Stufe 4).
    rival_paid_share: number | null;
    rival_visits: number | null;
    // Nur gesetzt bei echtem Keyword-Beleg (achsen.kw > 0) UND geprüftem Alter — die
    // NAMENSNENNUNG braucht den Beleg (§ 6 UWG), die Zahlen oben nicht.
    text_name: string | null;
    text_grund: string | null;
  } | null;
  site_kind: string | null;   // advertiser | publisher | marketplace | other | unknown
  business_desc: string | null;
  is_ecommerce: number | null;
  ecommerce_platform: string | null;
  // (fraud_score / classification retired from the surface · publisher-weighted, misleading
  //  for advertisers; still stored server-side, dormant for a future advertiser-fit rework)
  // bookkeeping
  source: string | null;   // tranco | crux_dach | meta_ads | both · where the domain was sourced
  enrichment_status: string;
  enrichment_error: string | null;
  enriched_at: string | null;
  people_found_at: string | null;  // set when people-search ran for this lead
  people_provider: string | null;  // 'serper' (free) | 'apify' (paid) · last people-search provider
  people_paid_at: string | null;   // stamped only on a PAID (apify) people-search run
  people_json: LeadPerson[] | null;  // found decision-makers, persisted directly on the lead
  has_marketing_decision_maker?: boolean | null;  // Nr. 22: ist ein Marketing-Entscheider dabei? null = noch keine Suche

  // Karten-Split (`UMBAU.md`, `person_stacks_enabled`): nur gesetzt, wenn der Server diese Zeile für
  // wartend/anschreiben/nachfassen personen-genau aufgeklappt hat (api/routers/lead_gen.py
  // `_expand_person_stack_items`) — sonst beide `null`/undefined, byte-identisch zu vorher. `person`
  // ist der konkrete Eintrag aus `people_json`, `person_key` seine Identität (LinkedIn-URL, sonst
  // Name — Spiegel von `db_web.lead_gen._person_key`).
  person?: LeadPerson | null;
  person_key?: string | null;

  linkedin_company_url: string | null;  // resolved LinkedIn company NAME-cache ('' = none found)
  parent_company_name: string | null;   // setter override: parent/holding for a subsidiary (NorthData)
  // Impressum extraction (free homepage→Impressum fetch + regex/Haiku)
  impressum_json: ImpressumRecord | null;     // structured company record (decoded server-side)
  impressum_checked_at: string | null;        // extraction stamp (TTL guard)
  impressum_wrong_at: string | null;          // setter flagged company/GF as wrong → suppress GF display
  northdata_checked_at: string | null;        // NorthData GF-scrape stamp (own TTL guard)
  linkedin_gf_url: string | null;             // GF /in/ profile (manually pasted now)
  linkedin_company_page_url: string | null;   // /company/ page (manually pasted now)
  linkedin_unresolvable_at: string | null;    // setter marked "no LinkedIn presence found"
  // Marketing decision-maker slot (source of truth, shared with Business Outreach)
  marketing_contact_name: string | null;
  marketing_linkedin_url: string | null;
  marketing_position: string | null;
  // Per-contact LinkedIn connection ("Freundschafts")-status + server-stamped offered_at
  marketing_contact_status: string | null;
  marketing_contact_offered_at: string | null;
  marketing_contact_offered_by: number | null;
  // Wieviele Freundschaftsanfragen bei dieser Firma schon rausgingen (1 = die erste läuft). Bestimmt,
  // welche Stufe der Resurfacing-Kadenz gilt · siehe frResurfaceThreshold in useLeadQueue.
  marketing_fr_offer_count?: number | null;
  // Aufgelöster Leitkraft-Account-Name des Offerers (server-seitig via _attach_offerer) · zeigt bei
  // angefragten/in-Bearbeitung befindlichen Leads, WEM der Lead gehört. Nur gesetzt wenn offered_by da ist.
  marketing_contact_offered_by_display_name?: string | null;
  // Welcher benannte LinkedIn-Account die FR gesendet hat (Client-Wahl aus dem Picker, beim Angebot
  // gesetzt) + server-aufgelöster Name (via _attach_linkedin_account) · auf der Karte statt der Person.
  marketing_linkedin_account_id?: number | null;
  marketing_linkedin_account_name?: string | null;
  // Setter-Flow Temperatur: server-gestempelt bei „verbunden"-Markierung des Marketing-Slots
  // (Goldenes-Fenster-Boost, Decay über flow_temp_half_life_hours); Revert löscht den Stempel.
  marketing_contact_connected_at?: string | null;
  linkedin_gf_status: string | null;
  linkedin_gf_offered_at: string | null;
  linkedin_gf_offered_by: number | null;
  linkedin_company_status: string | null;
  linkedin_company_offered_at: string | null;
  linkedin_company_offered_by: number | null;
  // Head-of-Sales decision (tri-state, orthogonal to status; at/by stamped server-side).
  // angenommen: hos_approved=1 · abgelehnt: hos_rejected_at set · offen: both clear.
  hos_approved: number;
  hos_approved_at: string | null;
  hos_approved_by: number | null;
  hos_rejected_at: string | null;
  hos_rejected_by: number | null;
  // Aufgelöst vom Server (_attach_hos_rejecter): WER abgelehnt hat. Ohne das sieht man im
  // Abgelehnt-Stapel nur, DASS abgelehnt wurde · und genau das WER ist dort die Information.
  hos_rejected_by_display_name?: string | null;
  // Phase 1 · K1a — das STAPEL-ETIKETT vom Server (db_web/lead_stacks.stage_of). Eine der neun
  // Stufen ('handtuch'|'abgelehnt'|'telefon'|'pruefen'|'nachfassen'|'anschreiben'|'wartend'|'neu'|
  // 'suchen') oder 'keine', wenn die Karte in keinen Stapel gehört. Kommt an der Liste UND am
  // SSE-Broadcast mit, weil `matchesStack` beim Live-Update daran entscheidet, ob eine Karte bleibt.
  // Optional, weil Lookup-/Cache-Antworten es nicht tragen · dort greift der alte Rechenweg als
  // Rückfall.
  // ⚠️ Korrigiert 2026-08-22 (Stufe 1a): „Klassik-Seite" stand hier zu Unrecht mit in der Klammer.
  // `lead_stacks.attach_stage(result["items"])` läuft in `GET /leads` UNBEDINGT
  // (api/routers/lead_gen.py:1285, alle drei Ladepfade laufen dort zusammen) — die Klassik-Tabelle
  // bekommt das Etikett also an jeder Zeile. Sie hat es bis zu dieser Stufe nur nicht gelesen.
  stage?: string | null;
  // Daily reservation (soft ownership). reserved_for = web_users.id; reserved_active = reserved_date is
  // today (server-computed, TZ-safe); username/display_name resolved server-side for the badge.
  reserved_for: number | null;
  reserved_date: string | null;
  reserved_active?: boolean;
  reserved_for_username?: string | null;
  reserved_for_display_name?: string | null;
  // Shared-mode ephemeral soft-lock (exclusive hold). lock_active = lock_until in the future; lock_by =
  // web_users.id; display_name resolved server-side for the "🔒 reserviert von …" badge.
  lock_by?: number | null;
  lock_until?: string | null;       // UTC 'YYYY-MM-DD HH:MM:SS' · seit dem Wegfall der Dauer ein Fern-Horizont
  lock_at?: string | null;          // wann beansprucht (ersetzt die weggefallene Restlaufzeit)
  lock_active?: boolean;
  lock_by_display_name?: string | null;
  // "Handtuch drauflegen": fraud0 hat den Lead für sich beansprucht (dauerhaft, kein Tages-Verfall).
  // fraud0_claimed_at gesetzt = Handtuch liegt; display_name server-seitig aufgelöst (Badge).
  fraud0_claimed_at?: string | null;
  fraud0_claimed_by?: number | null;
  fraud0_claimed_by_display_name?: string | null;
  // 3-stage setter pipeline: left-swipe ("passt nicht") parks the lead for admin review (≠ disqualified);
  // pitch_sent flag gates the Anschreiben-stage right-swipe (own flag, distinct from marketing_contact_status).
  setter_rejected_at: string | null;
  setter_rejected_by: number | null;
  setter_reject_reason?: string | null;  // optionaler 1-Tasten-Ablehnungsgrund (Undo-Snackbar-Chips)
  cooldown_until?: string | null;        // Recycling: auto-verlorener No-Reply-Lead kommt danach zurück
  marketing_pitch_sent_at: string | null;
  marketing_pitch_sent_by: number | null;
  // Pitch-Sprache (de/en), am Lead persistiert; null = noch nie gewählt → Best-Effort-Default aus country.
  marketing_pitch_lang: string | null;
  // Follow-up ("Nachfassen") stage: rounds sent after the pitch while awaiting a reply.
  marketing_followup_count: number | null;   // completed follow-up rounds (0 = only the pitch went out)
  marketing_followup_last_at: string | null;  // when the last round was sent (drives the N-day snooze)
  marketing_followup_by: number | null;
  marketing_reply_at: string | null;          // prospect replied → leaves the follow-up flow
  // Shared message style · set on the pitch, inherited by every follow-up round (one tone/signature).
  // tpl/fuTpl = gewählte Textvorlage für Pitch bzw. Nachfassen (siehe pages/fraud0/lib/pitch.ts);
  // beide optional, damit vor der Vorlagen-Auswahl gespeicherte Stile unverändert weiter gelten.
  marketing_msg_style: {
    anrede: 'du' | 'sie'; gruss: string; absender: string;
    lang?: 'de' | 'en'; enVariant?: 'standard' | 'direct'; tpl?: string; fuTpl?: string;
    mailLang?: 'de' | 'en'; mailAnrede?: 'du' | 'sie'; mailGruss?: string; mailAbsender?: string;
    mailTpl?: string; mailFuTpl?: string;
    // Welche Fassung je Mail-Baustein gewählt ist (siehe pages/fraud0/lib/mailBausteine.ts).
    // Optional wie tpl/fuTpl: vorher gespeicherte Stile gelten unverändert weiter.
    mailBausteine?: Record<string, string>;
  } | null;
  // Sende-Verlauf: welche Vorlage wann von wem rausging (server-gestempelt, nie vom Client geschrieben).
  marketing_msg_log: MsgLogEntry[] | null;
  // Phone-only call workflow: a manual reviewer marked the lead "nur Telefon, kein LinkedIn".
  // It then leaves the LinkedIn stacks and surfaces only in the dedicated "Telefon" stack.
  phone_only_at: string | null;
  phone_only_by: number | null;
  phone: string | null;                 // central, manually-editable number (prefilled from impressum.phone)
  // Additiv vom Server angehängt (attach_phones) — alle Nummern der Zentrale. `phone` oben bleibt
  // die abgeleitete Primärnummer.
  central_phones?: LeadPhone[];
  // Totmelder (E2): gesetzt, sobald der Lead keine einzige nicht-hart-defekte Nummer mehr hat.
  // `phone` bleibt trotzdem gefüllt (Anzeige) — nur der phone_only-Stapel filtert zusätzlich darauf.
  phone_dead_at?: string | null;
  call_status: string | null;           // disposition (see CALL_STATUSES); null = not called yet
  call_last_at: string | null;          // timestamp of the last call attempt
  callback_at: string | null;           // scheduled callback (future = snoozed out of the Telefon stack)
  // Sperr-Zustand (server-autoritativ, nur bei include_blocked gesetzt): warum die Karte gerade nicht
  // bearbeitbar ist und wann sie wieder frei wird. null = frei. Die Karte VERSCHWINDET dadurch nicht
  // mehr, sie rutscht ans Stapelende und sagt den Grund. block_until=null = kein Termin (z. B. „schaut
  // gerade jemand drauf" · das endet per SSE, nicht nach Uhrzeit).
  block_reason?: BlockReason | null;
  block_until?: string | null;
  call_notes: string | null;
  call_attempts: number | null;         // Anzahl „Jetzt anrufen"-Klicks (Zähler zum Verlauf)
  // E-Mail-Kanal: Adresse (vorbelegt aus impressum_json.email), Erst-Mail + Nachfass-Runden.
  // mail_followup_* ist die EIGENE Zählung des Mail-Kanals · marketing_followup_count treibt die
  // LinkedIn-Nachfassen-Queue und darf davon nie berührt werden (db_web/lead_gen.py).
  contact_email: string | null;
  mail_sent_at: string | null;
  mail_sent_by: number | null;
  mail_followup_count: number | null;
  mail_followup_last_at: string | null;
  mail_followup_by: number | null;
  // Computed on single-lead GET when no marketing contact is stored yet (does not persist)
  suggested_marketing_contact?: { name: string | null; linkedin_url: string | null; position: string | null } | null;
  notes: string | null;
  created_at: string;
  // Fraud0 deal-economics (computed on-read, services/fraud0_economics.py · not stored columns)
  tier: number | null;                  // Fraud0 tier 2–10 from estimated monthly sessions
  tier_fee_eur: number | null;          // annual Fraud0 fee for the tier
  value_density: number | null;         // PV per unit active effort = the prioritisation lens (info)
  exp_commission: number | null;        // expected EUR commission / started deal (nominal)
  pv: number | null;                    // present value of the expected commission
  roi: number | null;                   // value (refund + prevention) / fee; null if no paid signal
  roi_below_threshold: boolean | null;  // known roi below the active min_roi gate — badge, never hides the card (server-stamped)
  // Welche aktiven Richtlinien-Schwellen dieser Lead BEKANNT unterschreitet (server-gestempelt).
  // Seit 2026-08-05 gated die Schwelle nur noch den EINTRITT in „Neu"/„Kontakt suchen" — ein Lead
  // in Arbeit bleibt sichtbar und trägt stattdessen dieses Kennzeichen. null = keine gerissen.
  gate_below: Array<'roi' | 'paid_share' | 'recoverable' | 'search_spend'> | null;
  recoverable_eur: number | null;       // = refund_eur (search-PPC Google credit); kept for back-compat
  refund_eur: number | null;            // €/yr clawable from search-PPC click-fraud (Google auto-credit)
  prevented_waste_eur: number | null;   // €/yr blocked bot spend across paid channels (esp. display) · prevention path
  value_eur: number | null;             // refund_eur + prevented_waste_eur = the value that justifies the fee
  prevention_led: boolean | null;       // true = value mostly from blocked bot spend (prevention), not the search refund
  search_paid_spend_month_eur: number | null; // monthly search-paid ad spend = sessions × search_paid_share × cpc
  paid_share: number | null;            // total paid traffic share 0..1 (write-list gate input); null if no signal
  recoverable_share: number | null;     // share from recoverable channels (search_paid) · base for refund_eur
  paid_share_source: string | null;     // radeance | dataforseo | none · where paid-share came from
  economics_verdict: string | null;     // qualified | marginal | disqualified | roi_unavailable | no_sessions | …
  sessions_estimate: number | null;     // monthly sessions used for the tier
  sessions_source: string | null;       // similarweb | dataforseo | rank_proxy | none
  // ── CPC-Annahme + Anzeige-Band ──────────────────────────────────────────────────────────────────
  // Search-Spend/ROI sind MODELLIERT: gemessen sind nur Sessions und Paid-Anteil, der CPC ist gesetzt.
  // cpc_eur = der tatsächlich gerechnete Wert (Gate/Sortierung/econ_*), die *_low/high-Felder sind
  // reine Darstellung: die plausible Branchenspanne, damit die Scheingenauigkeit sichtbar wird.
  cpc_eur: number | null;               // €/Klick, auf dem alle Spend-/Wert-Zahlen beruhen
  cpc_low_eur: number | null;           // untere Branchen-Grenze (nur Anzeige)
  cpc_high_eur: number | null;          // obere Branchen-Grenze (nur Anzeige)
  cpc_range_source: string | null;      // category = Band der business_category | fallback = unklassifiziert
  cpc_outside_range: boolean | null;    // true = der gerechnete CPC liegt außerhalb der Branchenspanne
  search_paid_spend_month_low_eur: number | null;   // Spend am unteren Band-Ende
  search_paid_spend_month_high_eur: number | null;  // Spend am oberen Band-Ende
  roi_low: number | null;               // ROI am unteren Band-Ende
  roi_high: number | null;              // ROI am oberen Band-Ende
  // true = die Einstufung (qualified/marginal/disqualified) wechselt zwischen den Bandenden, hängt
  // also an der CPC-Annahme statt an den Daten. Server-seitig, weil roi_target nur der Engine bekannt ist.
  verdict_unstable: boolean | null;
  verdict_low: string | null;
  verdict_high: string | null;
}

// A found decision-maker, persisted on the lead (people_json). Rendered directly in the swipe card.
// ── Mehrere Telefonnummern je Person und je Zentrale (UMBAU.md Phase 4) ────────────────────────
// `lead_phones` (Backend) ist die alleinige Autorität für ALLE Nummern; `LeadPerson.phone` und
// `DomainLead.phone` bleiben abgeleitete, mitgeschriebene Spiegel der PRIMÄRnummer — sie werden
// NICHT ersetzt, weiterhin die einzigen Felder, die z.B. `_de_phone_check`/`phone_only` lesen.
// `phones`/`central_phones` kommen additiv vom Server dazu (`attach_phones`), NIE selbst zurück-
// schreiben (sonst sickert die Leseliste dauerhaft in `people_json`, der Server strippt sie zwar
// beim Schreiben, aber ein Frontend, das sie gar nicht erst mitschickt, ist der sauberere Weg).
// Der Personenschlüssel der Firmen-Zentrale — spiegelt `lead_gen.CENTRAL_PERSON_KEY` (Backend).
// Steht hier, weil ihn inzwischen drei Stellen im Frontend brauchen (PhoneList als Fallback-Träger,
// die Anruf-Historie zum Auflösen des Namens, künftig jede Ansicht, die Nummern gruppiert).
export const CENTRAL_PERSON_KEY = '__central__';

// Personen-Identität, wortgleich zu `lead_gen._person_key`: LinkedIn-URL, sonst Name — beides
// getrimmt und kleingeschrieben. Wird gebraucht, um `person_key` aus einem Anruf-Ereignis wieder
// einem Eintrag in `people_json` zuzuordnen; wer die Regel hier ändert, muss das Backend mitziehen.
export function personKeyOf(p: { linkedin_url?: string | null; name?: string | null }): string {
  return (p.linkedin_url ?? '').trim().toLowerCase() || (p.name ?? '').trim().toLowerCase();
}

// Den Marketing-Kontakt (per `personKeyOf`, wie `LeadStackCard.setPersonStatus`) in `people_json`
// finden und seinen Status mutieren — für den personen-genauen FR-Status-Klick
// (`Fraud0Leads.tsx` `saveConnStatus`). Ohne das schrieb der Klick NUR den Firmen-Skalar
// `marketing_contact_status`, und der Server leitet ihn bei jeder Firma MIT `people_json` aus der
// Personenliste ab (`_company_contact_state`, "bester Status gewinnt") — der Klick war dann
// wirkungslos, weil die Person im Update unverändert blieb.
// `null`, wenn keine Personenliste vorliegt oder der eingetragene Marketing-Kontakt darin nicht zu
// finden ist (Alt-Leads mit frei getipptem Namen/URL ohne Eintrag in `people_json`) — dann bleibt
// der bisherige reine Skalar-Pfad unverändert (der Server lässt einen geschriebenen Skalar stehen,
// solange die Personenliste leer/fehlend ist).
export function withUpdatedMarketingContactStatus(
  people: LeadPerson[] | null | undefined,
  contact: { name?: string | null; linkedin_url?: string | null },
  status: string,
): LeadPerson[] | null {
  if (!Array.isArray(people) || people.length === 0) return null;
  const key = personKeyOf(contact);
  if (!key) return null;
  const idx = people.findIndex((p) => personKeyOf(p) === key);
  if (idx < 0) return null;
  return people.map((p, i) => (i === idx ? { ...p, status } : p));
}

// Woher der laufende Anruf kam: die fraud0-Firma, die gewählte Nummer und die Person dahinter.
// `phone_key` ist die normalisierte Form (serverseitig gerechnet, `lead_gen.phone_key`) — nur mit
// ihr trifft ein späterer Defekt-Vermerk garantiert dieselbe Zeile, die gewählt wurde.
export interface DialKontext {
  lead_id: number;
  phone: string | null;
  phone_key: string | null;
  person_key: string | null;   // '__central__' = die Zentrale der Firma
  // WEN man anruft. Ohne das zeigte das Cockpit den Namen des Sales-CRM-Leads — und der gehört der
  // FIRMA: eine Firma hat genau einen verlinkten CRM-Lead, also erbte jede weitere Person die
  // Identität der ersten. Bei der Zentrale bleibt beides leer (man verlangt dort jemanden, man ruft
  // niemanden an), und die Anzeige fällt auf den Lead zurück.
  person_name?: string | null;
  person_position?: string | null;
  // Wer ausser mir zuletzt bei dieser Person gewählt hat; `open` = seither kein Abschluss, das
  // Gespräch läuft also vermutlich noch. Damit im Cockpit dabeisteht, dass ein Kollege gerade mit
  // demselben Menschen spricht — sonst rufen zwei nacheinander an und niemand weiss es.
  other_caller?: { name: string | null; at: string; open: boolean } | null;
}

export type PhoneDefectReason = 'nicht_vergeben' | 'falsche_nummer' | 'nicht_zustaendig' | 'dauerbesetzt_mailbox';

export const PHONE_DEFECT_REASONS: { value: PhoneDefectReason; label: string; hard: boolean }[] = [
  { value: 'nicht_vergeben', label: 'Nummer nicht vergeben', hard: true },
  { value: 'falsche_nummer', label: 'Falsche Nummer', hard: true },
  { value: 'nicht_zustaendig', label: 'Nicht zuständig', hard: true },
  { value: 'dauerbesetzt_mailbox', label: 'Dauerbesetzt / Mailbox', hard: false },
];

export interface LeadPhone {
  person_key: string;               // '__central__' = Firmen-Zentrale
  phone_key: string;
  phone: string;
  label: string | null;
  source: string | null;
  found_at: string | null;
  is_primary: number;               // SQLite 0/1
  defect_reason: PhoneDefectReason | null;
  defect_note: string | null;
  defect_by: number | null;
  defect_by_name: string | null;
  defect_at: string | null;
  last_dial_at?: string | null;
  last_status?: string | null;
  dial_count?: number;
}

// ── Telefon-Tabelle (Sales CRM) ────────────────────────────────────────────────────────────────
// 'central' = Impressum-Nummer der Firma (Zentrale), 'person' = privat/Lusha. Die Trennung läuft
// serverseitig über person_key, NICHT über `source`: von den Personennummern auf Prod stehen nur 14
// auf source='lusha', 36 auf 'manual' (von Hand auf der Karte eingetragen) — über `source` gefiltert
// verschwänden genau die 36.
export type CallTableKind = 'all' | 'central' | 'person';

export interface CallTablePhone extends LeadPhone {
  kind: 'central' | 'person';
  person_name: string;              // 'Zentrale' oder der Name aus people_json
}

// Eine Zeile ist eine PERSON, keine Firma: zwei Personen derselben Firma stehen an verschiedenen
// Stellen der Liste, weil „nie angerufen" und „ältester Anruf" je Person gerechnet werden.
// „Person sperren": ein aktiver Sperr-Eintrag aus do_not_call_person (mirror do_not_contact,
// nur personen- statt firmenskopiert). Siehe leadGenApi.setDoNotCallPerson/listDoNotCallPerson.
export interface DoNotCallPersonEntry {
  id: number;
  lead_id: number;
  person_key: string;
  reason: string;
  added_by: number | null;
  added_by_name: string | null;
  created_at: string;
  lifted_at: string | null;
  lifted_by: number | null;
  lifted_by_name: string | null;
}

export interface CallTableRow {
  row_key: string;                  // `${id}:${person_key}` — die Identität der ZEILE (React-key)
  id: number;                       // domain_leads.id — die Firma; daran hängt der Wählweg
  person_key: string;               // '__central__' = die Zentrale der Firma
  person_name: string;              // 'Zentrale' oder der Name aus people_json
  // true = der Server konnte NICHT feststellen, wem die Nummer gehört (Person umbenannt/entfernt).
  // `person_name` steht dann auf 'Person unbekannt' — bewusst kein geratener Name, denn die Zeile
  // ist die Auskunft, mit wem man gleich spricht.
  person_unknown?: boolean;
  person_position: string | null;   // Rolle, falls bekannt — wonach man entscheidet, wen man anruft
  domain: string;
  company: string;                  // Impressum-Firmenname, sonst die Domain
  category: string | null;
  status: string | null;
  // Die Wiedervorlage DIESER Zeile — erster Sortierschlüssel. `callback_at` steht zwar auf
  // domain_leads (ein Firmenfeld), aber seit 2026-08-12 rechnet der Server aus, wem der Termin
  // gilt: das `disposition`-Ereignis, das ihn gesetzt hat, nennt den `person_key`. Ein Termin, den
  // eine Kollegin mit jemand anderem im Haus vereinbart hat, steht hier deshalb auf null.
  callback_at: string | null;
  // …und taucht stattdessen hier auf: „im Haus ist gleich jemand verabredet". Wissenswert vor dem
  // Wählen, aber kein Grund, DIESE Zeile nach oben zu ziehen.
  company_callback_at?: string | null;
  // Wer ausser mir zuletzt bei dieser Person gewählt hat. `open` = seither wurde kein Abschluss
  // protokolliert, das Gespräch läuft also vermutlich noch. Kommt aus dem Ereignisstrom
  // (dial → disposition), nicht aus einer Anwesenheitsliste — deshalb kann es nicht hängenbleiben.
  other_caller?: { name: string | null; at: string; open: boolean } | null;
  call_status: string | null;
  call_notes: string | null;
  last_call_at: string | null;      // je PERSON · null = diese Person nie angerufen
  dial_count: number;               // je PERSON
  // Diese Person wurde gewählt, aber mindestens einer ihrer Anschlüsse war noch nie dran. Sie gilt
  // deshalb weiter als „nicht durchtelefoniert" und behält ihren Platz in der Liste, statt ans Ende
  // zu rutschen — sonst würde die zweite Nummer im Alltag nie gewählt.
  offene_nummer?: boolean;
  // Die Mail nach dem Gespräch. `mail_sent_at` = wann sie rausging (je Person; eine Mail ohne
  // benannte Person gilt dem ganzen Haus und steht in jeder Zeile der Firma). `mail_ask_at` = ab
  // wann die Tabelle nach der Antwort fragt — der Server rechnet ihn aus, damit hier keine zweite
  // Frist steht. `mail_antwort` = 'ja' | 'nein', null solange die Frage offen ist.
  mail_sent_at?: string | null;
  mail_ask_at?: string | null;
  mail_antwort?: 'ja' | 'nein' | null;
  phone_dead_at: string | null;     // Firma: keine einzige nicht-hart-defekte Nummer mehr
  // Team-Notizen der FIRMA — absichtlich nicht je Person: die Frage vor dem Wählen ist „gibt es hier
  // etwas zu beachten?", und die beantwortet auch eine Notiz an der Geschäftsführerin. `own` sagt
  // daneben, wie viele davon an genau dieser Person hängen.
  note_count: number;
  note_own_count: number;
  // „Im Haus wurde schon telefoniert": der jüngste Anruf bei einer ANDEREN Person derselben Firma.
  // Nicht zu verwechseln mit `other_caller` — das sieht gleich aus, meint aber DIESELBE Person und
  // nur fremde Anrufer. Hier zählt jeder Anruf, auch der eigene: der Betrieb hört den Pitch zum
  // zweiten Mal, egal wer ihn beim ersten Mal gehalten hat. Der Server liefert nur die jüngsten
  // Tage (HAUS_ANRUF_TAGE); `name` = null heisst „steht nicht mehr in people_json", dann sagt die
  // Anzeige „Jemand im Haus" statt einen Namen zu erfinden.
  // `call_status` = was bei DIESEM Anruf herauskam (aus CALL_DISPOSITIONS), `open` = gewählt und
  // seither nichts abgeschlossen (das Gespräch läuft vermutlich noch). Ohne das Ergebnis wäre die
  // Auskunft zweideutig — „vor 30 Min. angerufen" kann heissen, dass der Betrieb den Pitch schon
  // gehört hat, oder dass nur ein Freizeichen lief; das sind entgegengesetzte Ausgangslagen für
  // den eigenen Anruf. `call_status: null` bei `open: false` heisst „abgeschlossen ohne Ergebnis"
  // und ist ausdrücklich NICHT „nicht erreicht".
  house_call?: { person_key: string; at: string; name: string | null; position: string | null;
                 call_status: string | null; open: boolean } | null;
  phones: CallTablePhone[];         // nur die Anschlüsse DIESER Person
  // Virtuelle Zeile (2026-08-28): Lusha hat eine Nummer zu dieser Person, aber sie ist noch
  // nicht aufgedeckt — `phones` ist dann `[]`. Statt der Nummern-Chips zeigt die Spalte einen
  // Hol-Knopf; `lusha_reveal_credits` ist der Preis (0 = gratis, bereits einmal aufgedeckt).
  lusha_pending: boolean;
  lusha_has_mobile: boolean;
  lusha_reveal_credits: number | null;
  lusha_known: boolean | null;
}

export interface CallTableResult {
  rows: CallTableRow[];
  total: number;
  // Personenzahlen je Art. Eine Zeile ist entweder Zentrale oder Person, nie beides — anders als
  // früher (Firmenzahlen) addieren sich die beiden deshalb sauber auf `all`.
  counts: { all: number; central: number; person: number };
  kind: CallTableKind;
  limit: number;
  offset: number;
}

// ── Mail-Tabelle ───────────────────────────────────────────────────────────────────────────────
// Gegenstück zur Telefon-Tabelle, gleiche Zeilenform: eine Zeile ist eine PERSON, keine Firma.
// 'central' = info@/kontakt@ aus Impressum oder Hotline, 'person' = eine namentliche Adresse.
export type MailTableKind = 'all' | 'central' | 'person';

export interface MailTableEmail {
  id: number;
  lead_id: number;
  person_key: string;
  email_key: string;
  email: string;
  label: string | null;
  source: string | null;
  found_at: string | null;
  is_primary: number;
  defect_reason: string | null;
  defect_note: string | null;
  defect_at: string | null;
  source_index: number;
}

export interface MailTableRow {
  row_key: string;                  // `${id}:${person_key}` — die Identität der ZEILE (React-key)
  id: number;                       // domain_leads.id — die Firma; daran hängt der Versandweg
  person_key: string;               // '__central__' = die Zentrale der Firma
  person_name: string;              // 'Zentrale', der Name, oder 'Person unbekannt'
  person_unknown: boolean;          // der Server konnte NICHT feststellen, wem die Adresse gehört
  person_position: string | null;
  is_central: boolean;
  domain: string;
  company: string;                  // Impressum-Firmenname, sonst die Domain
  category: string | null;
  status: string | null;
  // Steht der UWG-Riegel für diese Firma offen (§ 7 Abs. 2 Nr. 2)? false = „Jetzt senden" würde
  // der Server abweisen. Bestimmt die Reihenfolge NICHT, ist nur die Auskunft an der Zeile.
  darf_mail: boolean;
  mail_sent_at: string | null;
  mail_count: number;
  mail_antwort_at: string | null;
  mail_antwort: string | null;
  mail_followup_count: number | null;
  mail_followup_last_at: string | null;
  call_status: string | null;
  callback_at: string | null;
  emails: MailTableEmail[];         // nur die Adressen DIESER Person
}

export interface MailTableResult {
  rows: MailTableRow[];
  total: number;
  erlaubt_total: number;            // wie viele Zeilen heute wirklich rausgehen dürfen
  kind: MailTableKind;
  limit: number;
  offset: number;
  nur_erlaubt: boolean;
}

// ── Kalender · "Heute dran" (Kalender-Umbau Schritt 4/5) ──────────────────────────────────────
// Eine Zeile je (Firma, Person, Kanal) mit dem naechsten Termin — gerechnet von
// kalender_zeilen() (db_web/lead_gen.py), nichts gespeichert. Mail steht bewusst NICHT in
// KALENDER_KANAELE: die Kadenz dafuer kommt erst in einem spaeteren Umbauschritt.
//
// 'linkedin_anbieten' (Nr. 88): Personen, denen wirklich eine Anfrage zusteht (`not_offered`) —
// budgetrelevant, verbraucht FR-Kontingent. 'linkedin_fr' zeigt dagegen Personen, denen SCHON eine
// Anfrage geschickt wurde ("Annahme prüfen") — kostet kein Budget. 'linkedin_anschreiben'
// (Nr. 88-Folgeschritt, 2026-08-25): verbunden, aber PERSONEN-GENAU noch kein Pitch — ebenfalls
// kein Budget (keine Freundschaftsanfrage geht raus).
export type KalenderKanal =
  'linkedin_anbieten' | 'linkedin_anschreiben' | 'linkedin_fr' | 'linkedin_nachfassen' | 'telefon';

export interface KalenderZeile {
  lead_id: number;
  domain: string;
  person_key: string;
  person_name: string | null;
  person_position: string | null;
  person_linkedin_url: string | null;
  kanal: KalenderKanal;
  naechster_termin: string | null;
  // 'fremd' (nur 'linkedin_fr') heisst: eine ANDERE Person derselben Firma wurde zuletzt
  // angefragt, diese Zeile kennt deshalb keinen eigenen Termin (`naechster_termin: null`) —
  // fail-closed, kein Firmen-Rueckfall. 'haus_kadenz'/'freigabe' (nur 'linkedin_anbieten') sagen,
  // woher das Datum kommt, wenn noch niemand im Haus angefragt wurde. Die Zeile bleibt in jedem
  // Fall sichtbar und MUSS als "unbekannt" markiert werden, nicht ausgeblendet.
  termin_quelle: 'person' | 'firma' | 'fremd' | 'haus_kadenz' | 'freigabe' | null;
  letzte_beruehrung: string | null;
  runde: number | null;
  termin_faellig: boolean;
  // Sende-Account dieser Zeile (Nr. 88): `null` = frei/unzugeordnet, kein Fehler — siehe
  // `sende_account_id`-Parameter unten. 'ereignis' ist eine staerkere Aussage als 'firma'
  // (abgeleitet aus dem AKTUELLEN Sender der Firma, kann bei Uebergabe veraltet sein).
  sende_account_id: number | null;
  account_quelle: 'ereignis' | 'firma' | null;
  // Teil B2 (2026-08-25), nur 'linkedin_anbieten': ein ANDERER Mensch hat dieser Person in den
  // letzten 15 Minuten schon ein Angebot notiert (Zeitpunkt als ISO-String) — reiner Hinweis, kein
  // Riegel. Der eigentliche Schutz gegen einen Doppel-Kontakt sitzt serverseitig.
  fremd_in_arbeit: string | null;
  // EIN Feld fuer alle Gruende, aus denen eine Zeile sichtbar, aber nicht bearbeitbar ist
  // (Nr. 102a/L6 — "kein zweites Badge-System"). 'handtuch' (Welle 2, L6, 2026-08-26): die Firma
  // ist bei fraud0 beansprucht — frueher ein harter Ausschluss, jetzt sichtbar + ans Ende sortiert.
  // 'fremdes_konto' (Nr. 104, 2026-08-26): die Zeile gehoert einem anderen, bekannten Sende-Konto
  // als das gewaehlte — ersetzt den frueheren serverseitigen WHERE-Ausschluss (siehe
  // `kalender()`-Kommentar unten). 'fremdbesitz' (Nr. 97, Personen-Besitz) ist vorbereitet, aber
  // der Riegel existiert noch nicht — kommt nie vor. Gilt fuer alle vier LinkedIn-Kanaele, NIE fuer
  // 'telefon'.
  sperr_grund: 'handtuch' | 'fremdes_konto' | 'fremdbesitz' | null;
  // Firmenlogo, dieselbe Quelle/Zwischenspeicher wie im Business-Outreach-CRM
  // (`services/bilder.py`, Tabelle `outreach_bilder`) — hier vom Kalender-Endpunkt angehaengt,
  // NICHT von `kalender_zeilen()` selbst. `null` = noch kein Logo im Zwischenspeicher, `CrmBild`
  // faellt dann auf farbige Initialen zurueck. Bewusst NUR Firmenlogos, keine Personen-Fotos.
  bild: string | null;
  bild_durchsichtig: boolean;
}

export interface KalenderResult {
  items: KalenderZeile[];
  total: number;
  limit: number;
  offset: number;
}

export interface LeadPerson {
  name: string | null;
  position: string | null;
  linkedin_url: string | null;
  confidence: number | null;  // company-resolution confidence; < 0.5 → ⚠ unverified flag
  note: string | null;        // resolution note (tooltip on the ⚠)
  status?: string | null;     // per-contact Freundschafts-Status: not_offered | pending | connected
  department?: string | null; // per-contact Abteilung/Rolle (gf | marketing | …), frei in people_json
  phone?: string | null;       // enriched mobile number (Apify, pay-on-success) · null = keine gefunden
  phone_source?: string | null;// woher die Nummer kam (apify | …)
  phone_found_at?: string | null;
  // Lushas Auskunft, WAS es zu der Person hat — nicht die Nummer selbst (die kostet 125× so viel und
  // holt der Setter in der Extension). lusha_checked_at ist zugleich die Wiederhol-Sperre.
  lusha_known?: boolean | null;        // kennt Lusha die Person überhaupt?
  lusha_has_phone?: boolean | null;    // … und hat eine Nummer hinterlegt?
  lusha_has_mobile?: boolean | null;   // … davon eine Mobilnummer?
  lusha_reveal_credits?: number | null;// was das Aufdecken kostet (0 = schon aufgedeckt)
  lusha_checked_at?: string | null;    // gesetzt = geprüft, wird nie ein zweites Mal bezahlt
  // Von Hand gesetzt (Schalter in der Kontaktzeile): hat Lusha zu DIESER Person eine Nummer?
  // Schlägt den bezahlten Prüflauf · wer in der Extension nachgesehen hat, weiß es zuletzt.
  // null/undefined = nie angefasst (dann zählt der Prüflauf, sofern er lief).
  lusha_manual?: boolean | null;
  // Von Hand gesetzt: hat diese Person KEINE deutsche Nummer? Unabhängig von lusha_manual (eine
  // Nummer kann existieren und trotzdem nicht deutsch sein) — damit später gefiltert werden kann,
  // wenn jemand keine Nicht-DE-Nummern anrufen will. null/undefined = nicht geprüft.
  non_de_phone?: boolean | null;
  // In welcher Sprache muss man diese Person anrufen? Einer aus PERSON_LANGUAGES.
  // null/undefined = nie angesehen — ausdrücklich etwas ANDERES als 'other' („nachgesehen, und es
  // ist keine der fünf"). Unabhängig von `non_de_phone`: das ist eine Aussage über die NUMMER,
  // das hier eine über den MENSCHEN.
  language?: string | null;
  // E-Mail dieser Person. `contact_email` am Lead ist die der FIRMA und bleibt davon unberührt.
  email?: string | null;
  // Additiv vom Server angehängt (attach_phones) — alle Nummern dieser Person, inkl. Defekt-Status
  // und Anruf-Kennzahlen je Nummer. `phone` oben bleibt die abgeleitete Primärnummer.
  phones?: LeadPhone[];
}

// Die Sprachen, in denen jemand angerufen werden kann — wortgleich zu `PERSON_LANGUAGES` in
// db_web/lead_gen.py. Dieselbe Liste trägt zwei Fragen: „welche Sprache braucht diese Person"
// (an der Person) und „welche spreche ich" (am Konto, Profil-Seite). 'other' gilt nur für die
// Person: „geprüft, keine der fünf" — als Konto-Sprache wäre es sinnlos.
export const PERSON_LANGUAGES = [
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'en', label: 'Englisch', flag: '🇬🇧' },
  { code: 'es', label: 'Spanisch', flag: '🇪🇸' },
  { code: 'it', label: 'Italienisch', flag: '🇮🇹' },
  { code: 'fr', label: 'Französisch', flag: '🇫🇷' },
  { code: 'other', label: 'andere Sprache', flag: '🌐' },
] as const;

export const CALL_LANGUAGE_CODES = PERSON_LANGUAGES.filter((l) => l.code !== 'other');

export function languageLabel(code: string | null | undefined): string | null {
  if (!code) return null;
  return PERSON_LANGUAGES.find((l) => l.code === code)?.label ?? null;
}

// Ergebnis der Nummern-Anreicherung (enrich-phones): pro Lead wie viele Kontakte eine Nummer bekamen.
export interface EnrichPhonesResult {
  lead_id: number;
  updated: number;        // Kontakte, die neu eine Nummer bekamen
  found: number;          // Nummern vom Actor geliefert
  candidates: number;     // angefragte /in/-Kontakte ohne Nummer
  skipped_no_url: number; // Kontakte ohne /in/-URL (nicht anreicherbar)
  cost_usd: number;       // realer pay-on-success Spend
  people: LeadPerson[];   // frische people_json (mit Nummern) zum sofortigen Anzeigen
}

// Ergebnis der Lusha-Prüfung: was Lusha hat, ohne dass etwas aufgedeckt wurde.
export interface LushaCheckResult {
  lead_id: number;
  updated: number;         // Personen, deren Auskunft in people_json geschrieben wurde
  checked: number;         // tatsächlich geprüfte Personen
  with_phone: number;      // davon mit hinterlegter Nummer
  already_checked: number; // übersprungen, weil früher schon geprüft (wird nie doppelt bezahlt)
  skipped_no_url: number;  // ohne /in/-URL → für Lusha nicht identifizierbar
  credits_used: number;    // echte Differenz aus /account/usage, keine Schätzung
  people: LeadPerson[];    // frische people_json zum sofortigen Anzeigen
}

export interface ImpressumPerson { name: string; role: string | null; }
export interface ImpressumRecord {
  company_name: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  register: string | null;
  vat_id: string | null;
  people: ImpressumPerson[];
}
export interface ExtractImpressumResult {
  cached: boolean;
  impressum: ImpressumRecord | null;
  impressum_url?: string | null;
  method?: string;   // 'regex' | 'haiku' | 'none'
  note?: string | null;
  checked_at?: string;
}

export interface ScrapeNorthdataResult {
  cached: boolean;
  people: ImpressumPerson[];
  count: number;
  company_name: string | null;
  register: string | null;
  source_url?: string | null;
  cost_usd?: number;
  checked_at?: string;
}

export interface ProviderCost {
  usd: number;
  label: string;   // e.g. "~$0.001 · 1 Credit" / "~$0.12 · 5 Profile" (computed server-side)
}

export interface BatchCost {
  startup_usd: number;   // actor-start, paid ONCE for the whole batch run
  per_lead_usd: number;  // marginal profiles cost per lead
  label: string;
}

export interface TargetRoles {
  seniorities: string[];
  core: string[];
  verticals: Record<string, string[]>;  // business_category → preset titles
  cost_estimates?: { serper: ProviderCost; apify: ProviderCost; apify_batch?: BatchCost };
}

export interface RolePriorityEntry {
  rank: number;
  label: string;
  titles: string[];
  keywords?: string[];  // normalized MATCH keywords (round-tripped on save; matcher falls back to titles)
  search?: string[];    // Apify jobTitles override for this band (round-tripped on save; empty ⇒ titles[:2])
  fallback?: boolean;   // searched only when the marketing search returns 0
  decision_maker?: boolean;  // Nr. 22: dieses Band zählt als Marketing-Entscheider (Warn-Prüfung)
}

// Swipe-stack category priority (order = priority). key matches the server-side catalog (predicate SSOT).
export interface CategoryPriorityEntry {
  key: string;
  label: string;
  enabled: boolean;
}
export interface CategoryCatalogItem {
  key: string;
  label: string;
}

// NUR die Erfolgsform. Scheitert die Suche (nicht freigegeben / keine Firma / keine Credits …),
// wirft der Endpunkt 502 mit {code, message} · der api-Client packt `detail.message` in
// `ApiError.message` und `detail.code` in `ApiError.code`. Der Aufrufer braucht also KEIN ok-Feld,
// sondern einen catch. (`ok` wird serverseitig aus der Erfolgsantwort herausgefiltert und existiert
// hier nie · ein `if (!r.ok)` würde bei JEDEM Erfolg fälschlich anschlagen.)
export interface FindContactsResult {
  created: number;
  found: number;
  skipped_duplicates: number;
  cost_usd?: number | null;   // real spend (chargedEventCounts) for the UI toast
  linkedin_company_url: string | null;
  client_id: number | null;
  source?: string;   // which provider produced the contacts ("serper" | "apify")
}

// Eine Angebots-Historie-Zeile (linkedin_fr_events) für einen Lead.
export interface FrEvent {
  event_type: string;          // offered | rejected | accepted | reset | blocked
  account_id: number | null;
  offerer_name: string | null; // aufgelöster web_user (display_name/username)
  slot: string | null;         // marketing_contact | linkedin_gf | linkedin_company
  note: string | null;         // Kontaktname zum Event-Zeitpunkt ("angeboten an [Name]")
  occurred_at: string;         // ISO UTC
}

// Anruf-Verlauf (append-only, lead_call_events). Die Flach-Spalten am Lead (call_status/
// call_last_at/call_notes) sind Einzelwerte und werden überschrieben · hier bleibt jeder Versuch.
export interface CallEvent {
  id: number;
  event_type: 'dial' | 'disposition' | 'mail_sent' | 'mail_followup';
  username: string | null;
  call_status: string | null;   // nur bei 'disposition'
  phone: string | null;         // gewählte Nummer zum Klick-Zeitpunkt
  callback_at: string | null;
  note: string | null;
  person_key?: string | null;   // NULL bei Altzeilen — bewusst nicht geraten
  phone_key?: string | null;
  occurred_at: string;          // ISO UTC MIT 'Z' (bewusst, siehe lib/sent.ts parseServerTime)
}

// EIN Eintrag der vereinten Lead-Zeitachse (get_lead_events, db_web/lead_gen.py). `source`
// unterscheidet die vier Herkünfte — nur 'lead_event' trägt `action`/`field`/`prev_value`/
// `new_value`/`event_id`(=`id`) und ist damit rückholbar (undoLeadEvent). Die drei Insel-Quellen
// (Anruf/FR/Sendung) sind reine Anzeige, kein `undo`.
export interface LeadEvent {
  id: number;
  source: 'lead_event' | 'call_event' | 'fr_event' | 'msg_log';
  occurred_at: string;
  user_id?: number | null;
  username?: string | null;
  action?: string;          // nur source='lead_event': 'status'|'hos'|'claim'|'setter_reject'|'reply'
  field?: string | null;
  prev_value?: string | null;
  new_value?: string | null;
  undo_of?: number | null;
  event_type?: string;      // call_event/fr_event
  note?: string | null;
  tpl_label?: string;       // msg_log
  call_status?: string | null;   // nur source='call_event'
  phone?: string | null;         // nur source='call_event'
  person_key?: string | null;    // nur source='call_event', NULL bei Altzeilen
  phone_key?: string | null;     // nur source='call_event'
}

// EIN Eintrag der FIRMENHISTORIE (Sicht `lead_activity`, db_web/lead_activity.py) — nicht zu
// verwechseln mit LeadEvent darüber: das ist die ältere, engere Fassung derselben Idee (vier
// Quellen, in Python zusammengelegt) und speist weiterhin den Erledigt-Banner.
//
// `id` ist synthetisch (`quelle.id.art`, z. B. 'comment.412.created') — eine SQL-Sicht hat keinen
// Primärschlüssel, die Liste braucht trotzdem einen stabilen Schlüssel.
// `art` ist der Unterscheider, auf den die Anzeige schaltet. Eine neue Ereignisart = ein
// UNION-Zweig im Backend plus ein Symbol hier, sonst nichts.
// `username` ist NULL, wo die Quelle keinen Urheber trägt (people_found_at, verbunden, antwort) —
// die Anzeige schreibt dann „System" und erfindet keinen Namen.
export type LeadActivityArt =
  | 'kommentar' | 'anruf' | 'linkedin' | 'aenderung'
  | 'kontakte_gefunden' | 'freigabe' | 'hos_abgelehnt' | 'kontakt_angeboten'
  | 'pitch_gesendet' | 'verbunden' | 'antwort' | 'setter_abgelehnt' | 'angerufen' | 'mail_gesendet';

export interface LeadActivity {
  id: string;
  art: LeadActivityArt;
  lead_id: number;
  occurred_at: string;
  user_id?: number | null;
  username?: string | null;
  person_key?: string | null;
  payload: Record<string, unknown>;
}

// Einstellungen der Hotline-Mail (db_web/fraud0_email_config.py).
export interface EmailConfig {
  subject_prefix: string;
  x_min_spend_eur: number;   // Block X ab diesem Monats-Search-Spend (€)
  x_min_share: number;       // optionale zweite Schwelle auf den Paid-Anteil (0..1)
  y_min_share: number;       // Block Y ab diesem Display-Anteil (0..1)
  subject_order: 'acb' | 'abc';
  booking_url: string;       // leer = Terminzeile entfällt
  signature: string;
  gruss: string;
  absender: string;
  // Terminfenster für den assumptiven CTA · EINE Zeile je Wochentag ("Mo 13-16"). Leer = der
  // Block fällt weg. Die konkreten Daten rechnet der Client aus, siehe buildSlots in coldEmail.ts.
  slot_plan: string;
  slot_lead_days: number;    // frühester Vorschlag erst so viele Tage nach heute
  // Prüft der Server vor dem Versand aus der App eine Einwilligung (§ 7 Abs. 2 Nr. 2 UWG)?
  // Seit 2026-08-19 standardmäßig aus, siehe db_web/fraud0_email_config.py.
  uwg_riegel: boolean;
}

export interface EmailConfigResponse {
  config: EmailConfig;
  references: Record<string, string[]>;  // Vertical → freigegebene fraud0-Referenzkunden
  verticals: string[];
  defaults: EmailConfig;
  max_references: number;
  // Zwei zusätzliche, feinere Sprossen VOR den Verticals (past-plans Stufe 2). Offene Schlüssel
  // (SimilarWeb sw_category/sw_category_group, keine feste Enum) — Default {}, unbefüllt verhält
  // sich die Fallback-Kette in referencesFor() exakt wie vor dieser Änderung.
  references_by_sw_category: Record<string, string[]>;
  references_by_sw_group: Record<string, string[]>;
}

// Gewichte + Schwellen der Wettbewerbs-Achse (past-plans Stufe 3, db_web/lead_competitors_config.py).
// Ändern dieser Werte wirkt erst nach dem nächsten Lauf von jobs/leadgen_competitors.py.
export interface CompetitorsConfig {
  gewicht_keyword: number;
  gewicht_kategorie: number;
  gewicht_groesse: number;
  gewicht_markt: number;
  gewicht_shop_system: number;
  min_score: number;
  top_n: number;
  max_df: number;
  // Default an: ein bereits angeschriebener Wettbewerber wird nicht angezeigt, der nächstbeste
  // rückt nach (siehe _attach_top_competitor in db_web/lead_gen.py).
  verberge_angeschriebene_wettbewerber: boolean;
}

// Team-weite eigene Pitch-Vorlage: Titel wird zum Button, body enthält {{Bausteine}} (siehe pitch.ts
// PITCH_TOKENS), die pro Lead gefüllt werden. lang = optionaler Sprach-Hinweis (rein informativ).
export interface PitchTemplate {
  id: string;
  title: string;
  body: string;
  lang?: 'de' | 'en';
  updated_at?: string;
  // Nur bei MAIL-Vorlagen gesetzt (eigener Namensraum, gleicher Server-Sanitizer mit with_subject).
  // Bei Pitch-Vorlagen bleibt es undefined · der Editor blendet das Feld dort aus.
  subject?: string;
}

// Override einer FESTEN Mail-Vorlage (Kalt/Hotline) · Betreff + Body mit {Platzhaltern}.
export interface EmailBuiltinOverride {
  subject: string;
  body: string;
}

// Vorlagen eines anderen Users (zum Übernehmen), gruppiert nach Besitzer.
export interface PitchTemplateGroup {
  owner_id: number;
  owner_name: string;
  templates: PitchTemplate[];
}

// Team-Kommentar zu einem Lead (Kommentar-Sektion) · Autor-Name + Zeit aus web_users gejoint.
export interface LeadComment {
  id: number;
  lead_id: number;
  body: string;
  created_at: string;          // ISO UTC (datetime('now'))
  author_id: number | null;
  author_name: string | null;  // display_name/username
  author_avatar_color?: string | null;  // Avatar-Farbe des Autors (web_users)
  parent_id: number | null;    // null = Top-Level, sonst Antwort auf diese Kommentar-id
  // Bezug (Karten-Split): null = gilt der ganzen Firma; gesetzt = gehört GENAU dieser Person.
  // person_name ist die Anzeige-Momentaufnahme fürs Etikett „zu Fabrice" (bleibt lesbar, auch wenn
  // die Person später umbenannt oder vom Lead entfernt wird).
  person_key?: string | null;
  person_name?: string | null;
}

export interface BulkFindContactsResult {
  processed: number;
  created_total: number;
  found_total: number;
  skipped_total: number;
  skipped_already_searched: number;
  cost_usd_total: number;
  source: string;
  per_lead: Array<{ lead_id?: number; ok: boolean; created?: number; skipped?: string; message?: string }>;
}

export interface FunnelStats {
  total: number;
  by_status: Record<string, number>;
  by_classification: Record<string, number>;
  by_enrichment_status: Record<string, number>;
  needs_protection: number;
  estimated_monthly_fraud_loss: number;
  measured: number;            // Tier-1 SimilarWeb-visits scraped (size known)
  qualified_with_mix: number;  // qualified (in-band) AND has a paid signal (Tier-2 mix or DFS paid)
  neu_supply: NeuSupplyRunway; // Mindestbestand-Warnung: reicht der genehmigte "Neu"-Nachschub noch?
}

// Nur die drei Zahlen der Klassik-Kopfzeile. Der volle Satz kostet zehn Voll-Scans (gemessen 4,7s),
// diese drei zusammen 0,05s -- und der Rest wird ausserhalb des Einstellungen-Drawers nirgends
// gelesen. Siehe db_web.lead_gen.get_funnel_stats.
export type FunnelStatsHeader = Pick<FunnelStats, 'by_status' | 'measured' | 'qualified_with_mix'>;

// Verbrauchsrate-basierte Mindestbestand-Warnung für den "Neu"-Stapel (siehe db_web.lead_gen.get_neu_supply_runway).
export interface NeuSupplyRunway {
  available: number;           // HoS-genehmigte, noch nicht angebotene Leads
  daily_rate: number;          // Ø FRs/Tag (letzte 7 Tage)
  days_remaining: number | null; // available / daily_rate; null wenn daily_rate = 0
  buffer_days: number;         // Warnschwelle (Tage Vorlauf)
  warn: boolean;               // days_remaining <= buffer_days
}

// Pro-Firma-Kontakt-„Meldebestand" (siehe db_web.lead_gen.get_contact_restock_list). READ-ONLY.
export type ContactRestockState = 'healthy' | 'needs_restock' | 'cooling' | 'exhausted' | 'connected';
export interface ContactRestockItem {
  id: number;
  domain: string;
  state: ContactRestockState;          // nur actionable States (needs_restock/exhausted/cooling) in items
  bank: number;                        // Kontakte in people_json
  tried: number;                       // bereits angebotene (Angebots-Historie)
  remaining: number;                   // nutzbar = Bank minus angeboten minus abgelehnt
  days_since_last_offer: number | null;
}
export interface ContactRestockCapacity {
  daily_fr_capacity: number;       // Σ effektiver FR-Tageslimits aktiver Sende-Accounts (Warm-up-bewusst)
  n_accounts: number;
  n_warming_up: number;
  total_usable_contacts: number;   // nutzbare Kontakte über ALLE aktiven Firmen
  contact_runway_days: number | null; // total_usable / daily_fr_capacity; null wenn Kapazität 0
}
export interface ContactRestockResponse {
  items: ContactRestockItem[];
  summary: Partial<Record<ContactRestockState, number>>;  // ALLE aktiven Firmen nach State gezählt
  config: { min_bench: number; cooling_days: number };
  capacity: ContactRestockCapacity;
}

// Admin-Sicht auf den GANZEN Lead-Pool: wie viele Leads haben wir, wie viele hat jede Person
// (Account-Inhaber), in welchem Stapel. Nötig, seit das harte Lead-Eigentum angebotene Firmen aus
// den Stapeln aller anderen entfernt.
export interface OwnerOverviewRow {
  owner_id: number;
  owner_name: string;
  wartend: number;
  anschreiben: number;
  nachfassen: number;
  beantwortet: number;
  gesamt: number;
}
export interface OwnerOverviewResponse {
  shared: { with_contact: number; without_contact: number; total: number };
  owners: OwnerOverviewRow[];
  totals: { owned: number; shared: number; all: number };
}

// Admin-Dashboard: pro Sende-Account die gesendeten FRs, aufgeschlüsselt nach Firma.
export interface AccountActivityPerson {
  name: string;       // Personenname aus dem FR-Log; "(ohne Name)" wenn leer
  offered: number;    // wie oft diese Person angefragt wurde (>1 = mehrfach)
}
export interface AccountActivityCompany {
  domain: string;
  offered: number;    // an diese Firma gesendete FRs
  accepted: number;   // davon angenommen
  last_at: string | null;
  people: AccountActivityPerson[];  // Aufschlüsselung je Person (zeigt Mehrfach-Anfragen)
}
export interface AccountActivityRow {
  account_id: number;
  account_name: string;
  owner_name: string | null;
  offered_today: number;
  offered_total: number;
  accepted_total: number;
  accept_rate: number | null;
  companies: AccountActivityCompany[];
}
export interface AccountActivityResponse {
  accounts: AccountActivityRow[];
}

export interface IcpBand {
  qualify_visits_min: number;
  qualify_visits_max: number;
}

// Range/band selection for the scrape-planner preview (subset of LeadGenFilters + band override).
export interface ScrapePreviewParams {
  runs_google_ads?: boolean | null;
  source?: string | null;
  site_kind?: string | null;
  business_category?: string | null;
  crux_present?: boolean | null;
  max_crux_magnitude?: number | null;
  min_crux_magnitude?: number | null;
  min_rank?: number | null;
  max_rank?: number | null;
  min_similarweb_visits?: number | null;
  max_similarweb_visits?: number | null;
  exclude_subdomains?: boolean | null;
  exclude_foreign_cctld?: boolean | null;
  exclude_small_market_crux?: boolean | null;
  band_min?: number | null;
  band_max?: number | null;
}

export interface ScrapePreview {
  pending: number;                    // unmeasured domains a scrape would hit (exact)
  sample_n: number;                   // already-measured leads in range = calibration sample
  inband_measured: number;
  inband_rate: number | null;         // empirical hit-rate in this range
  inband_rate_low: number;            // Wilson CI (confidence = width)
  inband_rate_high: number;
  expected_qualified: number | null;  // pending × rate (point estimate)
  expected_qualified_low: number | null;
  expected_qualified_high: number | null;
  cost_tier1_usd: number;
  cost_tier2_usd: number;             // follow-on Tier-2 mix cost for the expected qualified
  band_min: number;
  band_max: number;
  writeready_rate: number | null;     // historical paid-gate conversion among qualified-with-paid
  writeready_sample_n: number;
  writeready_confident: boolean;
  expected_writeready: number | null; // expected_qualified × writeready_rate (lower confidence)
}

// ── Funnel-Panel („Leads holen") ───────────────────────────────────────────────────────────────
// Eine Antwort, sechs Stufen in Funnel-Reihenfolge. Jede Zahl traegt ihre Herkunft mit, weil die
// alte Kopfzeile Zahlen aus drei verschiedenen Grundgesamtheiten unbeschriftet nebeneinander zeigte.
export interface FunnelErklaerung {
  title: string; was: string; quelle: string;
  berechnung: string; konfidenz: string; interpretation: string;
}

export interface FunnelStufe {
  key: string;
  /** Echter, UNGEDECKELTER Rueckstand. `null` = es gibt hier keinen (Datei- bzw. externe Quelle). */
  rueckstand: number | null;
  rueckstand_art: 'db' | 'datei' | 'extern';
  /** Was EIN Klick verarbeitet. Bei Stufe 4 aus dem Budget abgeleitet, keine Stueckzahl. */
  lauf_kappe: number | null;
  lauf_kappe_grund: string | null;
  naechster_lauf_stueck: number | null;
  preis_pro_stueck_usd: number;
  kosten_naechster_lauf_usd: number | null;
  kosten_ganzer_rueckstand_usd: number | null;
  /** Apify rechnet je Treffer ab — der Betrag ist eine Obergrenze, keine Rechnung. */
  kosten_sind_obergrenze: boolean;
  bezahlt: boolean;
  /** Folgt diese Zahl den Tabellenfiltern? Nur bei „Sessions holen" wahr. */
  filterabhaengig: boolean;
  umfang: 'rueckstand' | 'gesamt' | 'keiner';
  abdeckung: number | null;
  nach_quelle: Record<string, number> | null;
  nach_land: Record<string, number> | null;
  frische: { spalte: string; aeltester: string | null; neuester: string | null;
             nie_gestempelt: number } | null;
  erklaerung: FunnelErklaerung | null;
  // stufen-spezifisch
  bestand?: number;
  zuletzt_24h?: number;
  token_gesetzt?: boolean;
  rueckstand_offen?: number;
  rueckstand_wiederholung?: number;
  lauf_budget_usd?: number;
  ertrag?: ScrapePreview;
}

export interface FunnelPlan {
  stufen: FunnelStufe[];
  gesamt: { domain_leads: number; nach_quelle: Record<string, number>;
            nach_land: Record<string, number> } | null;
  /** Bestand „Kontakt gefunden" (gratis Impressum ODER bezahlt), ganze DB — vierte Zahl im Trichter. */
  bestand_kontaktierbar: number | null;
  preise: { waehrung: string; scrape_similarweb_pro_lead_usd: number;
            scrape_mix_pro_lead_usd: number };
  filter_aktiv: Record<string, unknown>;
  markt_hinweis: { gesperrte_endungen: number; art: string; hinweis: string };
  hinweise: string[];
  dauer_ms: number;
  cache: { treffer: boolean; alter_s: number; ttl_s: number };
}

// ── Rückwärts-Rechner („Ziel: kontaktierbare Leads/Tag") ────────────────────────────────────────
// Rechnet ein Tagesziel rückwärts durch dieselben Stufen wie `FunnelPlan` — wie viele Sessions/
// Marketing-Mix/Domains dafür nötig wären. Jede Rate ist live gemessen, keine Schätzung.
export interface ReverseFunnelStufe {
  key: string;
  /** Laufender Tagesbedarf, um das Ziel JEDEN Tag neu zu erreichen — nicht einmalig. */
  taeglich_noetig: number | null;
  taeglich_noetig_niedrig: number | null;
  taeglich_noetig_hoch: number | null;
  kosten_pro_tag_usd: number | null;
  rate: number | null;
  rate_n: number | null;
  rate_low?: number | null;
  rate_high?: number | null;
  /** Echter, JETZT gemessener Rückstand dieser Stufe (derselbe Wert wie im Vorwärts-Panel). */
  rueckstand_aktuell: number | null;
  lauf_kappe: number | null;
  laeufe_pro_tag_noetig: number | null;
  rueckstand_deckt_tag_ab: boolean | null;
  nach_land: Record<string, number> | null;
  land_umfang: 'rueckstand' | 'gesamt' | 'keiner' | null;
  hinweis: string;
}

export type ZielModus = 'taeglich' | 'einmalig';

export interface ReverseFunnelPlan {
  ziel_kontaktierbar_pro_tag: number;
  modus: ZielModus;
  stufen: ReverseFunnelStufe[];
  kosten_pro_tag_usd: number;
  kosten_pro_tag_usd_hoch: number;
  reicht_heute_fuer_einen_tag: boolean;
  hinweise: string[];
}

export interface TierMetric {
  tier: number;
  fee_year: number;
  win_rate: number;
  cycle_months: number;
  effort: number;
  exp_commission: number;
  discount_factor: number;
  pv: number;
  value_density: number;
  break_even_win_rate: number;
  min_paid_share: number;
}
export interface PortfolioAlloc {
  tier: number;
  starts_per_month: number;
  monthly_commission: number;
  concurrent_wip: number;
  value_density: number;
}
export interface TierDistribution {
  by_tier: Record<string, number>;
  no_size: number;
  total: number;
}
export interface Portfolio {
  active_capacity_per_month: number;
  effort_used: number;
  expected_monthly_commission: number;
  expected_annual_commission: number;
  concurrent_open_deals: number;
  meets_cash_floor: boolean;
  cash_floor: number;
  allocation: PortfolioAlloc[];
}
export interface FunnelRow {
  band: string;
  leads: number;
  connections: number;
  replies: number;
  booked: number;
  won_month: number;
  won_year: number;
  commission_year: number;
  value_per_lead: number;
}
export interface FunnelData {
  rows: FunnelRow[];
  rates: { connect_rate: number; reply_rate: number; booking_rate: number };
  total: { leads_month: number; booked_month: number; won_year: number; commission_year: number; per_lead: number };
}
export interface CapacityData {
  binding: 'leads' | 'closer';
  cap_calls: number;
  calls_needed_all: number;
  calls_used: number;
  utilization: number;
  monthly_commission: number;
  annual_commission: number;
  deals_won_per_month: number;
  deals_won_per_week: number;
  leads_per_month: number;
}
export interface EconomicsData {
  commission_rate: number;
  discount_annual: number;
  tiers: TierMetric[];
  portfolio: Portfolio;             // theoretical (config leads_per_month)
  portfolio_pipeline: Portfolio;    // real current pipeline (qualified-lead supply ceiling)
  hazard: { p0: number; hazard_monthly: number; r2: number };
  funnel: FunnelData;
  optimum_mix: { best_band: string | null; value_per_lead: [string, number][]; note: string };
  capacity: CapacityData;
  // Per-stage provenance of the funnel rates: 'measured' = from real Business-Outreach activity,
  // 'placeholder' = config default (sample still too thin). funnel_sample = denominator per stage.
  funnel_source: { connect_rate: 'measured' | 'placeholder'; reply_rate: 'measured' | 'placeholder'; booking_rate: 'measured' | 'placeholder' };
  funnel_sample: { connect: number; reply: number; booking: number };
  lead_distribution: TierDistribution;
  qualified_distribution: TierDistribution;
}

// Threshold-calibration buckets: how many paid-signal leads survive each cutoff.
export interface EconomicsDistribution {
  total_with_signal: number;
  paid_share: { min: number; count: number }[];      // min ∈ {0,.05,.10,.15,.20,.30}
  recoverable_eur: { min: number; count: number }[];  // min ∈ {0,6k,12k,25k,50k,100k}
  verdicts?: { qualified: number; marginal: number; disqualified: number; prevention_led: number }; // reacts to prevention_eff
  truncated: boolean;
}

// Gestufter Filter-Trichter (Einstellungen): wie der qualifizierte Pool Schritt für Schritt schrumpft.
export interface LeadFunnelStage { key: string; label: string; count: number; drop: number }
export interface LeadFunnel {
  stages: LeadFunnelStage[];
  start: number;
  final: number;        // inkl. der unbewerteten (ans Ende sortiert)
  unmeasured: number;   // ROI unbekannt · im Stapel ans Ende, nicht verworfen
  truncated: boolean;
}

export interface LeadGenFilters {
  /** Die generische Filterleiste (services/leadFilterApi) — dieselbe wie in der Anrufliste. */
  filter?: LeadFilter | null;
  // Phase 1 · K1b — der Stapel als NAME statt als Filter-Bastelei. Der Server übersetzt ihn in
  // GENAU die Parameter, die `filtersFor` heute von Hand zusammensetzt (db_web/lead_stacks).
  // Noch NICHT vom Stapel benutzt: `filtersFor` bleibt unverändert, bis der Abgleichlauf auf
  // Staging grün ist. Bis dahin ist das hier die Leitung, über die der Abgleichlauf fragt.
  stack?: string | null;
  // Die ZEILENART (Klassik Stufe 2, E2 — db_web.view_configs.ZEILENARTEN). `zeilenart: 'firma'`
  // unterdrückt die Personen-Aufklappung von wartend/anschreiben/nachfassen IMMER, unabhängig von
  // Rolle und `person_stacks_enabled` — Klassik ist eine Firmentabelle und würde dieselbe Firma
  // sonst mehrfach zeigen. Ohne den Parameter ändert sich nichts (der Wisch-Stapel schickt ihn
  // nie); ungültiger Wert → 400. Das ist der Rückweg.
  zeilenart?: string | null;
  // Ausnahmepfad für „Neu" (Inventar Nr. 18): läuft der freigegebene Nachschub leer, fällt das
  // HoS-Gate, damit der Stapel nicht leer dasteht. Muss ausdrücklich anforderbar bleiben.
  neu_fallback?: boolean;
  status: string | null;
  client_id: number | null;
  enrichment_status: string | null;
  search: string;
  max_rank: number | null;
  min_rank: number | null;
  crux_present: boolean | null;
  min_sessions: number | null;
  max_sessions: number | null;
  site_kind: string | null;
  business_category: string | null;
  // Mehrere Branchen gleichzeitig (Komma-Liste) · der Branchen-Filter des Search-Spend-Stapels.
  // Leer/weggelassen = keine Einschränkung; `business_category` (Einzelwert) bleibt daneben bestehen.
  business_categories?: string | null;
  runs_google_ads: boolean | null;
  has_meta_pixel: boolean | null;
  has_gtm: boolean | null;
  has_conversion_events: boolean | null;
  min_advertiser_score: number | null;
  max_crux_magnitude: number | null;
  min_crux_magnitude: number | null;   // size FLOOR: drop too-big buckets (top-1k/10k); opt-in, default off
  min_similarweb_visits: number | null;
  max_similarweb_visits: number | null;
  exclude_foreign_cctld: boolean;   // drop clearly-foreign ccTLDs (.it/.fr/.ua/…); default on
  exclude_subdomains: boolean;      // drop sub-domains (SimilarWeb can't measure them); default on
  exclude_small_market_crux: boolean; // hide UNMEASURED CrUX leads from small markets (CH/AT/LI); default on
  measured: boolean | null;         // audit view: only leads with a SW number, incl. disqualified
  source: string | null;            // source filter (e.g. 'meta_ads' = daily signal-first trickle)
  missing_linkedin: boolean | null; // setter work queue: still needs LinkedIn links (+ not unresolvable)
  reserved_mine: boolean | null;    // "Meine Leads": only leads reserved for the caller today
  reserved_for: number | null;      // ADMIN inspect: leads reserved for a SPECIFIC user today
  offered_by?: number | null;       // "bearbeitet von" (wartend/anschreiben): admin=any user, self=own id only
  // Sichtbarkeit am gewählten Sende-Account: „unbesessen ODER von diesem Inhaber". Unterschied zu
  // offered_by (Gleichheit): scope_owner LÄSST die noch freien Leads durch · sonst wäre „Neu" leer.
  scope_owner?: number | null;
  // „Search-Spend"-Stapel: Nur-Lese-Sicht über ALLE internen User (hebt die Eigentums-/Sperr-/
  // Reservierungs-Ausschlüsse auf). Server-seitig auf admin/setter/setter-junior/worker beschränkt.
  all_owners?: boolean;
  marketing_linkedin_account_id?: number | null;  // Account-Scoping: nur Leads, deren FR über diesen Account lief
  hos_approved: boolean | null;     // Head-of-Sales sign-off: true=only approved, false=only open, null=all
  hos_status: 'approved' | 'rejected' | 'pending' | null;  // HoS tri-state filter (richer than hos_approved)
  fraud0_claimed?: boolean;         // "Handtuch"-Stapel: nur von fraud0 beanspruchte Leads
  fraud0_claimed_by?: number | null; // "Handtuch"-Stapel personen-scoped: nur die Handtücher DIESER Person (Server erzwingt es für Reviewer)
  // 3-stage setter pipeline + admin reject-review queue
  marketing_contact_status: string | null; // not_offered | pending | connected (stage filter)
  setter_rejected: boolean | null;  // null=ignore, false=hide rejected (work stacks), true=only rejected (admin)
  pitch_sent: boolean | null;       // Anschreiben-stage gate
  followup_due: boolean | null;     // Nachfassen stack: pitched, no reply, round due (pair with status='contacted')
  phone_only?: boolean | null;      // null=ignore, false=hide phone-only (LinkedIn stacks), true=only phone-only (Telefon stack)
  callback_due?: boolean | null;    // Telefon stack (alt): blendet zukünftige Rückrufe ganz aus
  has_phone?: boolean | null;       // Telefon stack: Zugehörigkeit über die Nummer statt per Hand-Markierung
  has_mail?: boolean | null;        // Mail-Kanal: contact_email ODER Adresse im Impressum
  callback_sort?: boolean | null;   // Telefon stack: nach nächster Anrufzeit ordnen (fällig → nie → Zukunft)
  // Zeit-/Belegungssperren (Nachfass-Frist, Rückruf, Abkühlphase, fremde Reservierung/Sperre/Präsenz):
  // statt die Karte verschwinden zu lassen, liefert der Server sie mit block_reason/block_until und
  // sortiert sie ans Stapelende. Ohne die Flags verhält sich die Abfrage exakt wie bisher.
  include_blocked?: boolean | null; // gesperrte Leads MITLIEFERN (markiert statt versteckt)
  blocked_only?: boolean | null;    // Umkehrung: NUR die gesperrten („Nur gesperrte"-Filter)
  needs_contact_search?: boolean | null;  // "Kontakt suchen"-Stapel: nur Leads ohne gelaufene Entscheider-Suche (people_found_at IS NULL)
  has_linkedin_contact?: boolean | null;  // Kopf-Filter "nur mit LinkedIn-Kontakt": true=nur Leads mit gefundenem Kontakt/Person
  german_impressum?: boolean | null;       // Kopf-Filter "nur deutsches Impressum": true=nur Leads mit deutschem Impressum
  german_first?: boolean | null;            // Sortierung: deutsche Impressen ganz oben (Telefon-Stapel)
  german_phone_first?: boolean | null;      // Sortierung: Leads mit deutscher Telefonnummer (Vorwahl) ganz oben
  min_roi: number | null;           // computed-ROI floor; badges (never hides) known-below-floor leads; unknown-ROI leads pass through either way (keep_unmeasured)
  // Write-list (outreach) gates · lead-intrinsic, computed server-side (NOT ROI). best_leads = preset.
  min_paid_share: number | null;     // ≥ this paid-traffic share (0..1) to stay on the write-list
  min_recoverable_eur: number | null;// ≥ this recoverable ad-fraud €/yr to stay on the write-list
  min_search_paid_spend: number | null; // ≥ this monthly search-paid ad spend € to stay on the list (off = null)
  recoverable_rate: number | null;   // live clawback-rate override (0..0.2); null = saved default
  prevention_eff: number | null;     // live prevention-effectiveness override (0..1); null = saved default
  cpc: number | null;                // live search CPC override €/click (0.05..20); null = saved default
  value_channels: string[] | null;   // paid channels that feed the value (default ['search_paid']); display/social opt-in
  best_leads: boolean;               // one-click preset: config-default floors + value_density sort
  category_sort: boolean;            // swipe-stack opt-in: order by configurable category priority (primary key)
  priority_connected?: boolean;      // HoS-Freigabe-Stapel: bereits angenommene Kontakte (connected) zuerst sortieren
  exclude_foreign_reserved: boolean; // swipe-stack opt-in: hide leads reserved for ANOTHER user today
  lead_ids?: string;                 // Live-Sync: Komma-IDs · „passt GENAU dieser Lead in diesen Stapel?" (mit count_only)
  sort_by: string;
  sort_dir: string;
  limit: number;
  offset: number;
}

// Daily-reservation distribution rules (admin-editable). per_user order = priority (first filled first
// when supply is scarce). Empty per_user → server falls back to the ENV LEAD_RESERVATION_* defaults.
/** Ein Wiedervorlage-Vorschlag im Anruf-Dialog („in 2h", „morgen früh", „in 3 Monaten").
 *  months/days/hours werden auf „jetzt" addiert; `at` (HH:MM lokal) setzt danach die Uhrzeit.
 *  Gerechnet wird das an EINER Stelle: web/src/lib/callbackPresets.ts. */
export interface CallbackPreset {
  label: string;
  months: number;
  days: number;
  hours: number;
  at: string | null;   // 'HH:MM' lokal, oder null = Uhrzeit bleibt wie sie fällt
  default: boolean;    // genau einer ist true — der ist beim Öffnen vorgewählt
}

export interface DistributionConfig {
  // countries = ordered ISO-cc preference (empty = all); order = priority (list order = fill priority)
  per_user: { user_id: number; username: string; count: number; countries: string[]; markets?: string[] }[];
  // Geoeffnete Maerkte (Tokens wie config.resolve_countries: 'dach', 'gb', 'nl', ...).
  // Werkseinstellung ['dach']. MUSS beim Speichern mitgeschickt werden — der Server-Sanitizer
  // setzt fehlende Keys auf den Default zurueck (gleiche Falle wie flow_enabled).
  markets?: string[];
  distribution_mode: 'priority' | 'even' | 'shared';  // priority = fill first user to cap; even = round-robin (fair when scarce); shared = one pool, no carve-up (lock-based ownership)
  /** @deprecated Der Zeitfaktor ist raus · eine Beanspruchung hält bis zur Freigabe. Feld bleibt nur,
   *  damit gespeicherte Alt-Configs sauber durchlaufen; es wird von nichts mehr gelesen. */
  lock_minutes?: number;
  focus_claim_seconds?: number;   // shared mode: sustained focus that auto-promotes to a lock (default 120)
  exclude_worked: boolean;   // don't re-hand already-worked/assigned leads
  exclude_red_roi: boolean;  // badge (not withhold) low-ROI leads for setters; unknown-ROI already passes through unbadged
  min_roi: number;           // the cutoff used when exclude_red_roi is on (ROI < this → badged "ROI unter Schwelle", still shown)
  // ── Runden-Kadenzen (2026-07-29): je Stapel eine Wartezeit PRO RUNDE ────────────────────────
  // Eintrag i = Tage nach der (i+1)-ten Runde; die LÄNGE der Liste ist die Runden-Obergrenze.
  // Leere Liste = Mechanik aus. Diese vier Listen sind die Wahrheit · die drei Skalare darunter
  // liefert der Server nur noch abgeleitet mit (fr_resurface_days = fr_cadence_days[0] usw.), damit
  // ältere Leser nichts Falsches sehen. Nicht mehr schreiben: sie werden beim Speichern ignoriert.
  fr_cadence_days?: number[];        // Wartend: Abstand bis die nächste Person derselben Firma dran ist
  followup_cadence_days?: number[];  // Nachfassen: Abstand zwischen 1., 2., 3. … Nachricht
  call_cadence_days?: number[];      // Telefon: Abstand zwischen den Anrufversuchen (auto-Wiedervorlage)
  recycle_cadence_days?: number[];   // Recycling: Abkühlzeit je Recycling-Runde
  // Telefon: die Zeiten, die im Anruf-Dialog zur Wahl stehen („wann wieder anrufen?"). Genau ein
  // Eintrag hat default=true und ist vorgewählt. Nie leer — der Server fällt sonst auf die Vorgabe
  // zurück, damit „Niemand rangegangen" ein einziger Tastendruck bleibt.
  callback_presets?: CallbackPreset[];
  // Optional, weil NUR gelesen: der Server liefert sie immer mit, der Client schickt sie nie zurück
  // (er sendet die Listen). Vorher `number` = Pflicht, was jeden Schreiber zwang, einen Wert zu
  // erfinden, der der Liste widersprechen konnte.
  fr_resurface_days?: number;   // abgeleitet aus fr_cadence_days[0]; 0 = off
  followup_delay_days?: number; // abgeleitet aus followup_cadence_days[0]
  max_followups?: number;       // abgeleitet: Länge von followup_cadence_days
  hide_foreign_reserved: boolean;  // stack: hide leads reserved for ANOTHER user today (keep unreserved + own)
  missing_linkedin_user_ids: number[];  // non-admin user ids allowed to see leads WITHOUT a found contact
  manual_check_top_user_ids: number[];  // user ids for whom "Manuell prüfen"-leads sort to the TOP
  linkedin_top_user_ids: number[];       // user ids for whom leads WITH a found LinkedIn contact sort to the TOP
  linkedin_contact_definition: 'contact' | 'people' | 'both';  // what counts as "LinkedIn contact" for top-sort + head filter
  // Der Telefon-Stapel ist am 2026-08-20 abgebaut (Nr. 13). Das Feld bleibt im Vertrag, damit
  // gespeicherte Konfigurationen nicht beim Lesen brechen — es steuert nichts mehr.
  telefon_stack_user_ids?: number[];
  flow_enabled?: boolean;    // Setter-Flow ("⚡ Flow"-Stapel) für setter/worker/admin; Default aus (Rollback ohne Deploy)
  warmup_worse_leads?: boolean;  // Warm-up-LinkedIn-Accounts bekommen schwächere Leads zuerst; Default an
  flow_wip_cap_wartend?: number;      // Flow: keine NEUEN Leads solange ≥ N FRs offen (wartend); 0 = aus
  flow_temp_half_life_hours?: number; // Flow: Halbwertszeit (h) des Goldenes-Fenster-Boosts nach „verbunden"
  recycle_cooldown_days?: number;   // Recycling: No-Reply-Leads nach N Tagen zurück in den Pool; 0 = aus
  handover_enabled?: boolean;         // Nr.27: statt „verloren" an einen unverbrauchten Sende-Account übergeben
  fr_daily_limit?: number;            // LinkedIn FR-Tages-Warnschwelle (Flow-HUD + AppShell-Toast + Outreach-Tracking)
  fr_weekly_limit?: number;           // LinkedIn FR-Wochen-Ziel (Flow-HUD)
  review_daily_limit?: number;        // fraud0-Freigabe: geteiltes Tages-Kontingent (Reviewer-„Neu" X/50, harter Stopp)
  neu_reorder_point?: number;         // Lead-Vorrat „Neu": ab hier Meldung „nachbestellen" an den Admin; 0 = aus
  neu_safety_stock?: number;          // Lead-Vorrat „Neu": ab hier dringlich (Notfall-Nachschub steht bevor); 0 = aus
  warmup_connections?: number;        // unter so vielen Connections gilt ein LinkedIn-Account als frisch (Warm-up); 0 = kein Warm-up
  warmup_daily_fr_limit?: number;     // FR-Tageslimit während des Warm-ups (schlägt jedes Account-Limit)
  golden_window_hours?: number;       // Frist, in der ein frisch verbundener Kontakt im Flow vorgezogen wird
  target_roles?: string[];            // Ziel-Positionen der manuellen X-Ray-Suche (teamweit); leer = System-Standard
  flow_reject_reasons?: string[];     // 1-Tasten-Ablehnungsgründe (Undo-Snackbar-Chips beim Links-Wisch)
  flow_block_order?: { stage: 'nachfassen' | 'anschreiben' | 'wartend' | 'neu'; limit: number }[];  // Flow-Blockreihenfolge + -größen
  // "Heute dran" (2026-08-28, docs/kalender-vorrangregeln.md Regel 0): ab wie vielen Tagen Verzug
  // eine Zeile JEDEN Kanal-Rang schlägt (aber weiterhin unter dem goldenen Fenster bleibt); 0 = aus.
  ueberfaellig_ab_tagen?: number;
  // Reihenfolge der fünf "Heute dran"-Schritte (kalender_zeilen()). Jeder Kanal genau einmal;
  // mehrere Kanäle mit demselben `rang` sind gültig und mischen dann rein nach Alter (wie
  // Anbieten/FR/Telefon in der Werkseinstellung, alle rang:3). Das goldene Fenster steht NICHT
  // hier — es ist ein Interrupt, kein Schritt, und schlägt jede Konfiguration.
  kalender_block_order?: {
    kanal: 'linkedin_anbieten' | 'linkedin_fr' | 'linkedin_anschreiben' | 'linkedin_nachfassen' | 'telefon';
    rang: number;
  }[];
}

// The GLOBAL write-list gate · economics floors + assumptions that control which leads reach the
// setter stacks AND the daily reservation (server: services/lead_gate). Shape mirrors viewConfigApi's
// ViewThresholds/ViewAssumptions (the same persisted values), exposed via the lead-gen endpoint so the
// drawer can edit them without going through the per-role view-config editor.
export interface WritelistGate {
  thresholds: { min_paid_share: number | null; min_recoverable_eur: number | null; min_search_paid_spend: number | null };
  assumptions: { recoverable_rate: number; prevention_eff: number; cpc_eur: number; value_channels: string[];
                 // display-only: {business_category: [low, high]} für das CPC-Band auf der Karte
                 cpc_ranges?: Record<string, [number, number]> };
}

// Der EINE Serializer fuer den ~80-Parameter-Filterkopf von `/leads`. Herausgezogen (Klassik
// Stufe 2, Chip-Oberflaeche), weil `/leads/stack-counts` EXAKT dieselben Parameter nimmt: waere
// die Liste dort nachgebaut, liefen Zaehler und Tabelle auseinander, sobald jemand nur eine der
// beiden pflegt. Die Warnungen unten gelten fuer BEIDE Aufrufer.
//
// Rein mechanisch aus `listLeads` verschoben, Zeile fuer Zeile unveraendert -- der Diff soll
// pruefbar bleiben. Festgenagelt von `leadGenApi.buildLeadQuery.test.ts`.
export function buildLeadQuery(
  f: Partial<LeadGenFilters> & { include_pending?: boolean; count_only?: boolean },
): URLSearchParams {
  const qs = new URLSearchParams();
  if (f.status) qs.set('status', f.status);
  if (f.client_id != null) qs.set('client_id', String(f.client_id));
  if (f.enrichment_status) qs.set('enrichment_status', f.enrichment_status);
  if (f.search) qs.set('search', f.search);
  if (f.max_rank != null) qs.set('max_rank', String(f.max_rank));
  if (f.min_rank != null) qs.set('min_rank', String(f.min_rank));
  if (f.crux_present != null) qs.set('crux_present', String(f.crux_present));
  if (f.min_sessions != null) qs.set('min_sessions', String(f.min_sessions));
  if (f.max_sessions != null) qs.set('max_sessions', String(f.max_sessions));
  if (f.site_kind) qs.set('site_kind', f.site_kind);
  if (f.business_category) qs.set('business_category', f.business_category);
  // ⚠️ Diese Liste ist eine AUFZÄHLUNG: ein Feld, das hier fehlt, wird still verworfen · der Stapel
  // lädt dann ungefiltert weiter, ohne dass irgendwo ein Fehler auftaucht (genau so ging der
  // Branchen-Filter beim ersten Anlauf ins Leere: Chip aktiv, Liste unverändert).
  if (f.business_categories) qs.set('business_categories', f.business_categories);
  // Phase 1 · K1b — genau deshalb steht der Warnblock darüber: `stack` gehört in DIESE Aufzählung,
  // sonst hätte der Stapel beim Umschalten stumm ungefiltert weitergeladen.
  if (f.stack) qs.set('stack', f.stack);
  // Aus demselben Grund wie `stack` eine Zeile höher (E2, Klassik Stufe 2): fehlte `zeilenart`
  // hier, klappte Klassik die Personen weiter auf und zeigte dieselbe Firma mehrfach — sichtbar
  // falsch, aber ohne jeden Fehler im Log.
  if (f.zeilenart) qs.set('zeilenart', f.zeilenart);
  // Aus demselben Grund wie `stack` eine Zeile höher: die generische Filterleiste gehört in
  // DIESE Aufzählung, sonst lädt die Liste stumm ungefiltert weiter — Bedingungen sichtbar,
  // Ergebnis unverändert. Genau so ging der Branchen-Filter beim ersten Anlauf ins Leere.
  if (f.filter?.alle?.length) qs.set('filter', JSON.stringify(f.filter));
  if (f.neu_fallback) qs.set('neu_fallback', 'true');
  if (f.runs_google_ads != null) qs.set('runs_google_ads', String(f.runs_google_ads));
  if (f.has_meta_pixel != null) qs.set('has_meta_pixel', String(f.has_meta_pixel));
  if (f.has_gtm != null) qs.set('has_gtm', String(f.has_gtm));
  if (f.has_conversion_events != null) qs.set('has_conversion_events', String(f.has_conversion_events));
  if (f.min_advertiser_score != null) qs.set('min_advertiser_score', String(f.min_advertiser_score));
  if (f.max_crux_magnitude != null) qs.set('max_crux_magnitude', String(f.max_crux_magnitude));
  if (f.min_crux_magnitude != null) qs.set('min_crux_magnitude', String(f.min_crux_magnitude));
  if (f.min_similarweb_visits != null) qs.set('min_similarweb_visits', String(f.min_similarweb_visits));
  if (f.max_similarweb_visits != null) qs.set('max_similarweb_visits', String(f.max_similarweb_visits));
  if (f.exclude_foreign_cctld) qs.set('exclude_foreign_cctld', 'true');
  if (f.exclude_subdomains) qs.set('exclude_subdomains', 'true');
  if (f.exclude_small_market_crux) qs.set('exclude_small_market_crux', 'true');
  if (f.measured != null) qs.set('measured', String(f.measured));
  if (f.source) qs.set('source', f.source);
  if (f.missing_linkedin) qs.set('missing_linkedin', 'true');
  if (f.reserved_mine) qs.set('reserved_mine', 'true');
  if (f.reserved_for != null) qs.set('reserved_for', String(f.reserved_for));
  if (f.offered_by != null) qs.set('offered_by', String(f.offered_by));
  // ACHTUNG, dieser Serializer ist eine WHITELIST: ein Filter, der hier fehlt, wird still nicht
  // gesendet · der Build bleibt grün und der Fix ist eine Attrappe. Beim Einbauen von scope_owner
  // genau so passiert.
  if (f.scope_owner != null) qs.set('scope_owner', String(f.scope_owner));
  if (f.all_owners) qs.set('all_owners', 'true');
  if (f.marketing_linkedin_account_id != null) qs.set('marketing_linkedin_account_id', String(f.marketing_linkedin_account_id));
  if (f.hos_approved != null) qs.set('hos_approved', String(f.hos_approved));
  if (f.hos_status) qs.set('hos_status', f.hos_status);
  if (f.fraud0_claimed != null) qs.set('fraud0_claimed', String(f.fraud0_claimed));
  if (f.fraud0_claimed_by != null) qs.set('fraud0_claimed_by', String(f.fraud0_claimed_by));
  if (f.marketing_contact_status) qs.set('marketing_contact_status', f.marketing_contact_status);
  if (f.setter_rejected != null) qs.set('setter_rejected', String(f.setter_rejected));
  if (f.pitch_sent != null) qs.set('pitch_sent', String(f.pitch_sent));
  if (f.followup_due != null) qs.set('followup_due', String(f.followup_due));
  if (f.phone_only != null) qs.set('phone_only', String(f.phone_only));
  if (f.callback_due) qs.set('callback_due', 'true');
  if (f.has_phone) qs.set('has_phone', 'true');
  if (f.has_mail) qs.set('has_mail', 'true');
  if (f.include_blocked) qs.set('include_blocked', 'true');
  if (f.blocked_only) qs.set('blocked_only', 'true');
  if (f.needs_contact_search != null) qs.set('needs_contact_search', String(f.needs_contact_search));
  if (f.has_linkedin_contact != null) qs.set('has_linkedin_contact', String(f.has_linkedin_contact));
  if (f.german_impressum != null) qs.set('german_impressum', String(f.german_impressum));
  if (f.german_first != null) qs.set('german_first', String(f.german_first));
  if (f.german_phone_first != null) qs.set('german_phone_first', String(f.german_phone_first));
  if (f.min_roi != null) qs.set('min_roi', String(f.min_roi));
  if (f.min_paid_share != null) qs.set('min_paid_share', String(f.min_paid_share));
  if (f.min_recoverable_eur != null) qs.set('min_recoverable_eur', String(f.min_recoverable_eur));
  if (f.min_search_paid_spend != null) qs.set('min_search_paid_spend', String(f.min_search_paid_spend));
  if (f.recoverable_rate != null) qs.set('recoverable_rate', String(f.recoverable_rate));
  if (f.prevention_eff != null) qs.set('prevention_eff', String(f.prevention_eff));
  if (f.cpc != null) qs.set('cpc', String(f.cpc));
  if (f.value_channels && f.value_channels.length) qs.set('value_channels', f.value_channels.join(','));
  if (f.best_leads) qs.set('best_leads', 'true');
  if (f.category_sort) qs.set('category_sort', 'true');
  if (f.priority_connected) qs.set('priority_connected', 'true');
  if (f.exclude_foreign_reserved) qs.set('exclude_foreign_reserved', 'true');
  if (f.lead_ids) qs.set('lead_ids', f.lead_ids);
  if (f.sort_by) qs.set('sort_by', f.sort_by);
  if (f.sort_dir) qs.set('sort_dir', f.sort_dir);
  if (f.limit != null) qs.set('limit', String(f.limit));
  if (f.offset != null) qs.set('offset', String(f.offset));
  if (f.include_pending) qs.set('include_pending', 'true');  // only /klassik needs scrape_pending/mix_pending
  // Badge counter: only the gated `total` is wanted → server returns it via a cheap indexed COUNT(*)
  // over the pre-computed econ_* columns, skipping the 5000-row fullset materialisation + category sort.
  if (f.count_only) qs.set('count_only', 'true');
  return qs;
}

export const leadGenApi = {
  getStats: (clientId?: number | null) =>
    api.get<FunnelStats>(`/lead-gen/stats${clientId != null ? `?client_id=${clientId}` : ''}`),

  // Kopfzeilen-Variante: rechnet serverseitig nur die drei angezeigten Zahlen (siehe FunnelStatsHeader).
  getStatsHeader: (clientId?: number | null) =>
    api.get<FunnelStatsHeader>(
      `/lead-gen/stats?scope=header${clientId != null ? `&client_id=${clientId}` : ''}`),

  // Admin-Sicht: Gesamt-Pool + Leads pro Person (Account-Inhaber). Read-only.
  getOwnerOverview: () => api.get<OwnerOverviewResponse>('/lead-gen/owner-overview'),

  // Admin-Dashboard: gesendete FRs pro Sende-Account, aufgeschlüsselt nach Firma. Read-only.
  getAccountActivity: () => api.get<AccountActivityResponse>('/lead-gen/account-activity'),

  // Pro-Firma-Kontakt-Meldebestand (read-only Erkennung, kein Spend).
  getContactRestock: (p?: { minBench?: number; coolingDays?: number }) => {
    const qs = new URLSearchParams();
    if (p?.minBench != null) qs.set('min_bench', String(p.minBench));
    if (p?.coolingDays != null) qs.set('cooling_days', String(p.coolingDays));
    const q = qs.toString();
    return api.get<ContactRestockResponse>(`/lead-gen/contact-restock${q ? `?${q}` : ''}`);
  },

  listLeads: (f: Partial<LeadGenFilters> & { include_pending?: boolean; count_only?: boolean }) => {
    const qs = buildLeadQuery(f);
    const q = qs.toString();
    // `fallback_active` (Klassik Stufe 2, E4 — past-plans/rustling-percolating-kite.md): True, wenn
    // der Server den Ausnahmepfad für `stack=neu` selbst ausgelöst hat (leerer freigegebener
    // Nachschub) ODER der Aufrufer `neu_fallback=true` selbst gesetzt hatte. Rein additiv — kein
    // bestehender Aufrufer liest das Feld.
    return api.get<{ items: DomainLead[]; total: number; scrape_pending: number; mix_pending: number; qualified: number; economics_roi_unavailable: number; fallback_active?: boolean }>(`/lead-gen/leads${q ? `?${q}` : ''}`);
  },

  // ── Klassik Stufe 2 · die beiden Chip-Endpunkte (Server seit Nr. 86, live geprüft Nr. 89) ──

  // E1 · Welche Filterfelder überschreibt ein Stapel? Statisch, kein DB-Zugriff, jede Rolle darf
  // lesen. Die Chip-Leiste braucht das, um kollidierende Bedienelemente (Klassiks HoS-Dropdown)
  // sichtbar zu DEAKTIVIEREN, statt sie stumm überstimmen zu lassen. Es kommen nur drei Schlüssel
  // vor: hos_status, fraud0_claimed, followup_due (db_web/lead_stacks._OVERRIDE_KEYS).
  stackMeta: () =>
    api.get<{ overrides: Record<string, Record<string, string | boolean>> }>('/lead-gen/stack-meta'),

  // E5 · Die Zähler für mehrere Stapel in EINEM Round-Trip, statt bis zu acht Einzelaufrufen.
  // Nimmt denselben Filterkopf wie `/leads` — deshalb `buildLeadQuery`, kein zweiter Serializer.
  //
  // ⚠️ `spend` NIE zusammen mit anderen Stapeln anfragen (Server antwortet 400). Sie braucht
  // `all_owners=true`, einen Aufrufer-Parameter, den `stack=` selbst nicht setzt: gebündelt gäbe
  // es entweder falsche Zahlen oder ein Sichtbarkeits-Leck (Nr. 89, gemessen 24 statt 223).
  // `spend` bekommt einen eigenen, alleinstehenden Aufruf.
  stackCounts: (stacks: string[], f: Partial<LeadGenFilters>) => {
    const qs = buildLeadQuery(f);
    qs.set('stacks', stacks.join(','));
    return api.get<{ counts: Record<string, number> }>(`/lead-gen/leads/stack-counts?${qs.toString()}`);
  },

  // Is a SimilarWeb scrape currently running server-side? Used on page-mount so a user returning
  // mid-scrape sees the "läuft…" state (the background task survives leaving the page).
  scrapeSimilarWebStatus: () =>
    api.get<{ running: boolean }>('/lead-gen/scrape-similarweb/status'),
  scrapeSimilarWebMixStatus: () =>
    api.get<{ running: boolean }>('/lead-gen/scrape-similarweb-mix/status'),

  getEconomics: (capacity?: number) =>
    api.get<EconomicsData>(`/lead-gen/economics${capacity != null ? `?capacity=${capacity}` : ''}`),

  // Threshold-calibration preview: over the current filter context (minus the two floors), how many
  // paid-signal leads survive each paid-share / recoverable cutoff. Feeds the Beste-Leads panel readout.
  // Sends the filter subset the endpoint declares (extra keys are ignored server-side).
  economicsDistribution: (f: Partial<LeadGenFilters>) => {
    const qs = new URLSearchParams();
    if (f.status) qs.set('status', f.status);
    if (f.business_category) qs.set('business_category', f.business_category);
    if (f.source) qs.set('source', f.source);
    if (f.search) qs.set('search', f.search);
    if (f.runs_google_ads != null) qs.set('runs_google_ads', String(f.runs_google_ads));
    if (f.has_meta_pixel != null) qs.set('has_meta_pixel', String(f.has_meta_pixel));
    if (f.has_gtm != null) qs.set('has_gtm', String(f.has_gtm));
    if (f.has_conversion_events != null) qs.set('has_conversion_events', String(f.has_conversion_events));
    if (f.min_advertiser_score != null) qs.set('min_advertiser_score', String(f.min_advertiser_score));
    if (f.crux_present != null) qs.set('crux_present', String(f.crux_present));
    if (f.min_sessions != null) qs.set('min_sessions', String(f.min_sessions));
    if (f.max_sessions != null) qs.set('max_sessions', String(f.max_sessions));
    if (f.min_rank != null) qs.set('min_rank', String(f.min_rank));
    if (f.max_rank != null) qs.set('max_rank', String(f.max_rank));
    if (f.site_kind) qs.set('site_kind', f.site_kind);
    if (f.max_crux_magnitude != null) qs.set('max_crux_magnitude', String(f.max_crux_magnitude));
    if (f.min_crux_magnitude != null) qs.set('min_crux_magnitude', String(f.min_crux_magnitude));
    if (f.min_similarweb_visits != null) qs.set('min_similarweb_visits', String(f.min_similarweb_visits));
    if (f.max_similarweb_visits != null) qs.set('max_similarweb_visits', String(f.max_similarweb_visits));
    if (f.exclude_foreign_cctld) qs.set('exclude_foreign_cctld', 'true');
    if (f.exclude_subdomains) qs.set('exclude_subdomains', 'true');
    if (f.exclude_small_market_crux) qs.set('exclude_small_market_crux', 'true');
    if (f.measured != null) qs.set('measured', String(f.measured));
    if (f.recoverable_rate != null) qs.set('recoverable_rate', String(f.recoverable_rate));
    if (f.prevention_eff != null) qs.set('prevention_eff', String(f.prevention_eff));
    if (f.cpc != null) qs.set('cpc', String(f.cpc));
    if (f.value_channels && f.value_channels.length) qs.set('value_channels', f.value_channels.join(','));
    const q = qs.toString();
    return api.get<EconomicsDistribution>(`/lead-gen/economics-distribution${q ? `?${q}` : ''}`);
  },

  // Gestufter Filter-Trichter für den Einstellungen-Drawer: wie viel jeder Schritt wegschneidet.
  leadFunnel: (f: Partial<LeadGenFilters>) => {
    const qs = new URLSearchParams();
    if (f.status) qs.set('status', f.status);
    if (f.runs_google_ads != null) qs.set('runs_google_ads', String(f.runs_google_ads));
    if (f.site_kind) qs.set('site_kind', f.site_kind);
    if (f.max_crux_magnitude != null) qs.set('max_crux_magnitude', String(f.max_crux_magnitude));
    if (f.min_crux_magnitude != null) qs.set('min_crux_magnitude', String(f.min_crux_magnitude));
    if (f.exclude_foreign_cctld) qs.set('exclude_foreign_cctld', 'true');
    if (f.exclude_subdomains) qs.set('exclude_subdomains', 'true');
    if (f.exclude_small_market_crux) qs.set('exclude_small_market_crux', 'true');
    if (f.min_paid_share != null) qs.set('min_paid_share', String(f.min_paid_share));
    if (f.min_roi != null) qs.set('min_roi', String(f.min_roi));
    if (f.recoverable_rate != null) qs.set('recoverable_rate', String(f.recoverable_rate));
    if (f.prevention_eff != null) qs.set('prevention_eff', String(f.prevention_eff));
    if (f.cpc != null) qs.set('cpc', String(f.cpc));
    if (f.value_channels && f.value_channels.length) qs.set('value_channels', f.value_channels.join(','));
    const q = qs.toString();
    return api.get<LeadFunnel>(`/lead-gen/lead-funnel${q ? `?${q}` : ''}`);
  },

  // Detail drawer: thread the live economics overrides so the breakdown matches the list (CPC/channels/rates).
  getLead: (id: number, f?: Partial<LeadGenFilters>) => {
    const qs = new URLSearchParams();
    if (f?.recoverable_rate != null) qs.set('recoverable_rate', String(f.recoverable_rate));
    if (f?.prevention_eff != null) qs.set('prevention_eff', String(f.prevention_eff));
    if (f?.cpc != null) qs.set('cpc', String(f.cpc));
    if (f?.value_channels && f.value_channels.length) qs.set('value_channels', f.value_channels.join(','));
    const q = qs.toString();
    return api.get<DomainLead>(`/lead-gen/leads/${id}${q ? `?${q}` : ''}`);
  },

  // `offered_by` is a write-only override for the FR offerer (not a DomainLead column); the server
  // stamps the matching *_offered_by slot.
  // `release_owner` ist ebenfalls write-only: „Firma abgeben". Seit 2026-07-30 überlebt der
  // Inhaber-Stempel einen gewöhnlichen Kontaktwechsel (er bezeichnet den INHABER, nicht „gerade
  // läuft eine Anfrage"), deshalb braucht die Freigabe einen ausdrücklichen Weg. Der Server lässt
  // sie nur für den Inhaber selbst oder einen Admin zu.
  updateLead: (id: number, data: Partial<DomainLead> & { offered_by?: number; release_owner?: boolean }) =>
    api.patch<DomainLead>(`/lead-gen/leads/${id}`, data),

  // Von-Hand-Nachtrag zu EINER Person (lusha_manual/non_de_phone) — der einzige Schreibpfad des
  // sonst nur-lesenden Search-Spend-Stapels. Atomar auf dem Server (Read-Modify-Write), damit ein
  // veralteter lokaler people_json-Stand keine fremde, zwischenzeitliche Änderung überschreibt
  // (siehe LeadStackCard.tsx, SpendPeopleList). Identität: linkedin_url, sonst name.
  // `phone` steht bewusst NEBEN den beiden Merkern: eine Nummer je Person war vorher nur über ein
  // clientseitig vollgeschriebenes people_json setzbar (setPersonPhone/CallWorkbench) — genau das,
  // wogegen dieser schmale Endpunkt gebaut wurde. Leer = löschen (der Server räumt die Herkunft mit).
  setPersonManualFields: (leadId: number, person: { linkedin_url?: string | null; name?: string | null },
                          fields: { lusha_manual?: boolean | null; non_de_phone?: boolean | null;
                                    phone?: string | null; language?: string | null;
                                    email?: string | null }) =>
    api.patch<DomainLead>(`/lead-gen/leads/${leadId}/people/manual`, {
      linkedin_url: person.linkedin_url ?? undefined,
      name: person.name ?? undefined,
      ...fields,
    }),

  // Eine manuell gefundene Person (Lusha, ohne Auto-Suche) anhängen — derselbe Read-Modify-Write auf
  // dem Server wie setPersonManualFields, aus demselben Grund: der Search-Spend-Stapel zeigt Leads
  // querbeet über alle Setter, ein clientseitig vollgeschriebenes people_json wäre ein Datenverlust-
  // Risiko (siehe LeadStackCard.tsx, AddPersonRow).
  //
  // `phone` darf gleich mit: ohne Nummer kein Anrufknopf und damit kein getrackter Anruf — der Fall,
  // der diesen Weg ausgelöst hat. `person_existed` in der Antwort meldet den Dedup-Treffer (URL,
  // sonst Name+Position), sonst sähe ein Klick auf eine schon vorhandene Person wie ein Klick ohne
  // Wirkung aus.
  addLeadPerson: (leadId: number, person: { name: string; position?: string | null; linkedin_url?: string | null; department?: string | null; phone?: string | null }) =>
    api.post<DomainLead & { person_existed?: boolean }>(`/lead-gen/leads/${leadId}/people`, person),

  // Stapel-freies Nachschlagen: findet JEDE Firma, auch die, die in keinen Stapel passt und einem
  // Kollegen gehört. Nur lesend, Suchbegriff Pflicht. Ersetzt in der Kopf-Suche die N parallelen
  // Stapel-Abfragen · die konnten per Bauart nur finden, was ohnehin in einem Stapel liegt.
  lookupLeads: (search: string, limit = 20) =>
    api.get<{ items: DomainLead[]; total: number }>(
      `/lead-gen/lookup?search=${encodeURIComponent(search)}&limit=${limit}`),

  // Firma übernehmen: stempelt die Zuständigkeit um (marketing_contact_offered_by) und vermerkt es
  // als Team-Kommentar. NICHT zu verwechseln mit `handoverLead` (anderer Sende-Account, frischer
  // Zyklus) oder `claimLead` (30-Minuten-Arbeitssperre) · hier geht es um „wem gehört die Firma".
  takeOverLead: (id: number) =>
    api.post<DomainLead>(`/lead-gen/leads/${id}/take-over`, {}),

  // Nr. 27 · Übergabe an einen anderen Sende-Account statt „verloren". Der Vorschlag ändert nichts;
  // erst `handoverLead` schreibt. `available: false` deckt beides ab: Schalter aus ODER wirklich am
  // Ende (kein unverbrauchter Account / nichts freizugeben) · die Karte braucht nur einen Pfad.
  getHandoverSuggestion: (id: number) =>
    api.get<{
      available: boolean;
      reason?: string;
      account?: { id: number; name: string; effective_daily_limit: number | null; is_warming_up: boolean; free_today: number };
      reopen_count?: number;
      has_capacity?: boolean;
    }>(`/lead-gen/leads/${id}/handover-suggestion`),
  handoverLead: (id: number, linkedin_account_id: number) =>
    api.post<{ ok: boolean; reopened: number; account_id: number }>(
      `/lead-gen/leads/${id}/handover`, { linkedin_account_id }),

  // „Beanspruchen"/Übernahme: ausschliesslicher Halt OHNE Dauer · er hält bis zur Freigabe.
  // 409 = jemand anderes hält ihn · 403 = Lead mit gefundenem Kontakt (nur Admin/fraud0).
  claimLead: (id: number) =>
    api.post<{ ok: boolean; lock_until: string; lock_at: string | null }>(
      `/lead-gen/leads/${id}/claim`, {}),
  // „Freigeben" · ausdrücklicher Klick. NICHT /blur: das droppt nur die Anwesenheit (jeder
  // Kartenwechsel feuert es) und würde die Beanspruchung sonst still auflösen.
  releaseClaim: (id: number) =>
    api.post<{ ok: boolean }>(`/lead-gen/leads/${id}/claim/release`, {}),
  blurLead: (id: number) =>
    api.post<{ ok: boolean }>(`/lead-gen/leads/${id}/blur`, {}),

  // Shared-mode live presence: heartbeat that I'm dwelling on this lead (top card). Server keeps it
  // in-memory + auto-claims after the configured focus window. Fire-and-forget every ~10s.
  focusLead: (id: number) => api.post<{ ok: boolean }>(`/lead-gen/leads/${id}/focus`, {}),
  // Best-effort release on card-change / tab-close. keepalive fetch survives navigation/unload and CAN
  // set the auth header (sendBeacon can't), so the normal header-auth /blur works. A missed blur is
  // harmless · the server's 25s presence TTL + lazy lock expiry self-heal.
  blurBeacon: (id: number) => {
    const token = localStorage.getItem('token') ?? '';
    fetch(`/api/lead-gen/leads/${id}/blur`, {
      method: 'POST', keepalive: true,
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: '{}',
    }).catch(() => {});
  },

  bulkStatus: (ids: number[], status: string) =>
    api.post<{ updated: number }>('/lead-gen/leads/bulk-status', { ids, status }),

  // Setter left-swipe ("passt nicht"): park for admin review (≠ disqualified). rejected=false rehabilitates.
  // reason = optionaler 1-Tasten-Ablehnungsgrund (Undo-Snackbar-Chips) · Trainingsdaten fürs ICP.
  setterReject: (id: number, rejected = true, reason?: string) =>
    api.post<DomainLead>(`/lead-gen/leads/${id}/setter-reject`, { rejected, ...(reason ? { reason } : {}) }),

  // Mark the pitch text as sent (Anschreiben-stage gate). sent=false clears it (undo).
  // tpl/tpl_label = welche Vorlage rausging → landet im Sende-Verlauf des Leads (marketing_msg_log).
  // `text` = der TATSÄCHLICH gesendete Text → Sende-Verlauf des Leads (kein Vorlagen-Nebeneffekt).
  // `lib` traegt die Bibliotheks-Herkunft ({lib_id, lib_root}) — muss an ALLEN VIER Sendepfaden
  // mitlaufen, sonst zaehlt die Bibliothek nur Erstkontakte und die Bewertung aus der Antwortrate
  // haette keine Basis.
  // `fmt: 'memo'` = es ging eine SPRACHNACHRICHT raus, `text` ist dann das Transkript (MsgLogEntry.fmt).
  // `person` (Nr. 88-Folgeschritt, 2026-08-25): OPTIONAL, letzter Parameter, damit kein
  // bestehender Aufrufer angefasst werden muss. Ohne ihn schreibt der Server nur die
  // Firmenspalten wie bisher; mit ihm zusaetzlich ein personen-genaues Ereignis, das
  // `kalender_zeilen()` fuer den Kanal "Anschreiben" braucht (siehe db_web/lead_gen.py
  // set_pitch_sent/record_followup).
  pitchSent: (id: number, sent = true, tpl?: string, tplLabel?: string, text?: string, lib?: Record<string, number>,
              fmt?: 'memo', person?: { key?: string; name?: string; linkedinAccountId?: number }) =>
    api.post<DomainLead>(`/lead-gen/leads/${id}/pitch-sent`, {
      sent, tpl, tpl_label: tplLabel, text, ...(lib ?? {}), ...(fmt ? { fmt } : {}),
      ...(person?.key ? { person_key: person.key, person_name: person.name,
                          linkedin_account_id: person.linkedinAccountId } : {}),
    }),

  // Nachfassen: record a follow-up round (sent=true bumps the count + re-snoozes; sent=false = undo).
  // `text` = der TATSÄCHLICH gesendete Text → Sende-Verlauf des Leads (kein Vorlagen-Nebeneffekt).
  // `person`: siehe pitchSent oben.
  followup: (id: number, sent = true, tpl?: string, tplLabel?: string, text?: string, lib?: Record<string, number>,
             fmt?: 'memo', person?: { key?: string; name?: string; linkedinAccountId?: number }) =>
    api.post<DomainLead>(`/lead-gen/leads/${id}/followup`, {
      sent, tpl, tpl_label: tplLabel, text, ...(lib ?? {}), ...(fmt ? { fmt } : {}),
      ...(person?.key ? { person_key: person.key, person_name: person.name,
                          linkedin_account_id: person.linkedinAccountId } : {}),
    }),

  // Nachfassen: mark that the prospect replied → leaves the follow-up flow. replied=false = undo.
  // `replyText`/`situation` sind der Feedback-Kanal: WAS zurückkam und WORAN es hängt. Beide
  // optional — eine Antwort ohne Wortlaut ist immer noch eine Antwort.
  markReply: (id: number, replied = true, replyText?: string, situation?: string) =>
    api.post<DomainLead>(`/lead-gen/leads/${id}/reply`, {
      replied,
      ...(replyText ? { reply_text: replyText } : {}),
      ...(situation ? { situation } : {}),
    }),

  // Woran hängt es, OHNE dass eine Antwort kam (Aufgeben-Grund, Ghosting). Ändert den Lead nicht.
  recordSituation: (id: number, situation: string, note?: string) =>
    api.post<{ ok: boolean }>(`/lead-gen/leads/${id}/situation`, {
      situation, ...(note ? { note } : {}),
    }),

  // Manual reviewer marks the lead "nur Telefon, kein LinkedIn" (admin). on=false undoes (back to pool).
  // An optional central number is stored at the same time.
  phoneOnly: (id: number, on = true, phone?: string) =>
    api.post<DomainLead>(`/lead-gen/leads/${id}/phone-only`, { phone_only: on, ...(phone ? { phone } : {}) }),

  // Record a call attempt on a phone-only lead (disposition + optional callback + note).
  // phone/personKey (UMBAU.md Phase 4): auf welche Nummer sich DIESE Disposition bezieht — ohne sie
  // fällt der Server auf den letzten eigenen Wählversuch, sonst auf die Zentrale zurück. Bei
  // falsche_nummer/nicht_zustaendig liefert die Antwort zusätzlich `suggest_defect`.
  logCall: (id: number, body: { call_status: string; callback_at?: string | null; notes?: string;
                                phone?: string | null; person_key?: string | null }) =>
    api.post<DomainLead & { suggest_defect?: {
      person_key: string | null; phone_key: string; phone: string; reason: PhoneDefectReason;
    } }>(`/lead-gen/leads/${id}/call-log`, body),

  // „Jetzt anrufen" geklickt → Versuch mit exakter Uhrzeit protokollieren.
  //
  // BEWUSST rohes fetch mit keepalive statt api.post: direkt nach diesem Aufruf folgt der
  // tel:-Sprung, der die Seite verlässt bzw. die App auf dem Handy in den Hintergrund schiebt -
  // ein normaler fetch würde dabei abgebrochen und der Versuch ginge verloren. keepalive lässt den
  // Request den Seitenwechsel überleben. sendBeacon kann hier nicht genutzt werden: es erlaubt
  // keinen Authorization-Header.
  // Antwort {skipped:true} = Server-Debounce hat den Klick als Doppelklick verworfen.
  // {blocked:true, reason} = die Nummer ist hart defekt und wurde NICHT gezählt (UMBAU.md Phase 4) —
  // die eigentliche Sperre sitzt im Client (PhoneList rendert dann keinen tel:-Link), diese Prüfung
  // schützt nur Zähler/Kadenz. opts.force (nach „Trotzdem anrufen") zählt den Versuch trotzdem, hebt
  // den Defekt aber NICHT auf.
  logDial: (id: number, phone?: string | null,
           opts?: { personKey?: string | null; force?: boolean }):
    Promise<{ skipped: boolean; blocked?: boolean; reason?: string; lead?: DomainLead } | null> => {
    const token = localStorage.getItem('token');
    return fetch(`/api/lead-gen/leads/${id}/call-dial`, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        phone: phone ?? null,
        person_key: opts?.personKey ?? null,
        force: opts?.force ?? false,
      }),
    })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);  // Tracking darf den Anruf NIE blockieren
  },

  // Anruf-Verlauf (append-only). Gegenstück zu frEvents.
  callEvents: (id: number) =>
    api.get<{ events: CallEvent[] }>(`/lead-gen/leads/${id}/call-events`),

  // Vereinte Zeitachse eines Leads (Status/HoS/Handtuch/Ablehnung/Antwort + Anrufe + LinkedIn-
  // Anfragen + Sendungen) · speist den Erledigt-Banner beim Zurückblättern im Stapel und den
  // „Verlauf"-Abschnitt der Karte.
  leadEvents: (id: number) =>
    api.get<{ events: LeadEvent[] }>(`/lead-gen/leads/${id}/events`),

  // Die Firmenhistorie: EIN Verlauf je Firma, neueste zuerst, seitenweise. Liest die SICHT
  // `lead_activity` — vierzehn Quellen, darunter die zehn Zeitstempel auf `domain_leads`, die
  // bisher nirgends als Verlauf auftauchten.
  leadActivity: (id: number, limit = 50, offset = 0) =>
    api.get<{ items: LeadActivity[]; total: number }>(
      `/lead-gen/leads/${id}/activity?limit=${limit}&offset=${offset}`),

  // Genau EIN Ereignis der Zeitachse zurücknehmen (Erledigt-Banner „Doch rückgängig"). Wirft
  // ApiError mit httpStatus 409, wenn sich der Lead seither geändert hat — der Aufrufer zeigt dann
  // eine Meldung statt fremde Arbeit zu überschreiben.
  undoLeadEvent: (id: number, eventId: number) =>
    api.post<DomainLead>(`/lead-gen/leads/${id}/events/${eventId}/undo`),

  // Mail rausgeschickt (sent=false = rückgängig). tpl/tplLabel/subject/text landen im Sende-Verlauf
  // des Leads (marketing_msg_log, kind='mail') · dasselbe Muster wie pitchSent.
  // `personKey`: an wen geschrieben wurde. Der Composer im Stapel schreibt ans Haus und lässt ihn
  // weg; aus dem Abschluss eines Gesprächs kommt er mit, damit die Telefon-Tabelle zwei Tage später
  // in DER Zeile nachfragt, in der telefoniert wurde.
  markMailSent: (id: number, sent = true, tpl?: string, tplLabel?: string,
                 subject?: string, text?: string, lib?: Record<string, number>,
                 personKey?: string) =>
    api.post<DomainLead>(`/lead-gen/leads/${id}/mail-sent`,
                         { sent, tpl, tpl_label: tplLabel, subject, text,
                           person_key: personKey, ...(lib ?? {}) }),

  // Die Nachfrage aus der Telefon-Tabelle: hat er auf die Mail geantwortet? „Ja" stempelt zusätzlich
  // `marketing_reply_at` (der Lead verlässt die Nachfassen-Strecke), „nein" hält nur fest, dass
  // gefragt wurde — sonst stünde dieselbe Frage morgen wieder da.
  setMailAntwort: (id: number, answered: boolean, personKey?: string) =>
    api.post<DomainLead>(`/lead-gen/leads/${id}/mail-reply`,
                         { answered, person_key: personKey }),

  // Nachfass-Runde PER MAIL (kind='mail_followup'). Zählt auf mail_followup_count · NICHT auf den
  // LinkedIn-Zähler marketing_followup_count, der die Nachfassen-Queue treibt.
  mailFollowup: (id: number, sent = true, tpl?: string, tplLabel?: string,
                 subject?: string, text?: string, lib?: Record<string, number>) =>
    api.post<DomainLead>(`/lead-gen/leads/${id}/mail-followup`,
                         { sent, tpl, tpl_label: tplLabel, subject, text, ...(lib ?? {}) }),

  // Hotline-Mail-Einstellungen + freigegebene Referenzkunden (lesend für alle).
  emailConfig: () =>
    api.get<EmailConfigResponse>('/lead-gen/email-config'),

  // Einstellungen und/oder Referenzkunden speichern (Admin). Alle Felder optional.
  saveEmailConfig: (body: {
    config?: Partial<EmailConfig>; references?: Record<string, string[]>; reset_references?: boolean;
    references_by_sw_category?: Record<string, string[]>; references_by_sw_group?: Record<string, string[]>;
  }) =>
    api.put<{
      config: EmailConfig; references: Record<string, string[]>;
      references_by_sw_category: Record<string, string[]>; references_by_sw_group: Record<string, string[]>;
    }>('/lead-gen/email-config', body),

  // Gewichte/Schwellen der Wettbewerbs-Achse (past-plans Stufe 3, db_web/lead_competitors_config.py)
  // — lesend für alle, dieselbe Erklärung wie beim email-config-Muster oben.
  competitorsConfig: () =>
    api.get<{ config: CompetitorsConfig; defaults: CompetitorsConfig }>('/lead-gen/competitors-config'),

  // Speichern (Admin). Ändert NICHT die bereits abgelegten Kanten — die entstehen erst mit dem
  // nächsten Lauf von jobs/leadgen_competitors.py.
  saveCompetitorsConfig: (body: Partial<CompetitorsConfig>) =>
    api.put<{ config: CompetitorsConfig }>('/lead-gen/competitors-config', body),

  // Build a Sales-CRM prefill from a phone-only lead (mirror of toOutreach, for the call pipeline).
  toSalesCrm: (id: number) =>
    api.post<LeadToSalesCrmResult>(`/lead-gen/leads/${id}/to-sales-crm`, {}),

  // Telefonklick → automatisch den verlinkten Sales-CRM-Lead finden/anlegen (ohne Formular). Der
  // Aufrufer öffnet mit dem zurückgegebenen Lead direkt CallCockpit (kein neues Tracking-System).
  // personKey reicht nur durch (log_dial) — keine eigene Blockade-Logik hier.
  // `dial` sagt, WELCHE Nummer welcher Person gerade gewählt wurde — das Cockpit braucht es für das
  // Ergebnis „Nummer falsch / nicht vergeben", sonst müsste es bei mehreren Anschlüssen raten.
  dialToSalesCrm: (id: number, phone?: string | null, contactName?: string | null, personKey?: string | null) =>
    api.post<{ lead: Lead; dial?: DialKontext }>(`/lead-gen/leads/${id}/dial-to-sales-crm`,
                            { phone, contact_name: contactName, person_key: personKey }),

  // Löst per CloudTalk-API einen echten Click-to-Call aus (klingelt zuerst das gemeinsame Gerät,
  // danach automatisch `phone`). Reiner Auslöser — Tracking/Cockpit-Öffnen übernimmt der Aufrufer
  // danach selbst über dialToSalesCrm, identisch zum bestehenden manuellen Anruf-Button.
  cloudtalkCall: (id: number, phone: string) =>
    api.post<{ ok: boolean }>(`/lead-gen/leads/${id}/cloudtalk-call`, { phone }),

  // Gegenstück zu dialToSalesCrm: „Verwerfen (nicht tracken)" nimmt den Wählversuch zurück, statt
  // ihn stehen zu lassen. Sonst wäre „nicht tracken" nur halb wahr — die Person galt trotzdem als
  // angerufen und rutschte in der Telefon-Tabelle ans Ende. `removed: false` ist kein Fehler,
  // sondern „es gab nichts zurückzunehmen" (Riegel in lead_gen.delete_dial).
  discardDial: (id: number, phone?: string | null, personKey?: string | null) =>
    api.post<{ removed: boolean }>(`/lead-gen/leads/${id}/discard-dial`,
                                   { phone, person_key: personKey }),

  // Telefon-Tabelle im Sales CRM: jede Firma mit mindestens einer Nummer, in Anruf-Reihenfolge.
  // NICHT der Telefon-Stapel — der hängt an `phone_only` (auf Prod 3 Leads), hier zählt „hat eine
  // Nummer" (216). NUR-LESE; angerufen wird über dialToSalesCrm wie überall sonst.
  // `filter` ist die Auswahl der Filterleiste als JSON (siehe services/leadFilterApi.ts). Sie
  // wirkt auf die Zeilen UND auf die drei Zähler über den Knöpfen — ein Zähler, der den Filter
  // nicht kennt, wäre keiner mehr.
  callTable: (params: { kind?: CallTableKind; limit?: number; offset?: number; search?: string;
                        filter?: LeadFilter | null; listenId?: number | null }) =>
    api.get<CallTableResult>('/lead-gen/leads/call-table'
      + `?kind=${params.kind ?? 'all'}&limit=${params.limit ?? 100}&offset=${params.offset ?? 0}`
      + (params.search ? `&search=${encodeURIComponent(params.search)}` : '')
      + (params.filter?.alle?.length
          ? `&filter=${encodeURIComponent(JSON.stringify(params.filter))}` : '')
      + (params.listenId ? `&listen_id=${params.listenId}` : '')),


  // Mail-Tabelle: jede PERSON mit mindestens einer Adresse, in Schreib-Reihenfolge. Liest aus
  // `lead_emails` und NICHT über listLeads — der grosse Listen-Weg versteckt Adressen hinter
  // Stapel, Scoping und Wirtschaftlichkeits-Riegel (gemessen: 620 gezeigt, 1.122 vorhanden).
  // NUR-LESE; geschickt wird über mailApi.senden wie überall sonst.
  mailTable: (params: { kind?: MailTableKind; limit?: number; offset?: number; search?: string;
                        nurErlaubt?: boolean }) =>
    api.get<MailTableResult>('/lead-gen/leads/mail-table'
      + `?kind=${params.kind ?? 'all'}&limit=${params.limit ?? 100}&offset=${params.offset ?? 0}`
      + (params.nurErlaubt ? '&nur_erlaubt=true' : '')
      + (params.search ? `&search=${encodeURIComponent(params.search)}` : '')),

  // "Heute dran": eine Zeile je (Firma, Person, Kanal) mit dem naechsten Termin. NUR-LESE; kein
  // Mail-Kanal (kommt erst in einem spaeteren Umbauschritt), `kanaele` = Teilmenge von
  // KalenderKanal, undefined = alle vier. `sendeAccountId` (Nr. 88) wirkt auf die drei
  // LinkedIn-Zweige "Annahme pruefen"/"Anschreiben"/"Nachfassen"; "Anfrage schicken" und Telefon
  // bleiben unberuehrt.
  // Seit Nr. 104 FILTERT er nicht mehr: eine fremde Zeile verschwindet nicht, sondern bekommt
  // `sperr_grund='fremdes_konto'` und wird serverseitig ans Listenende sortiert.
  kalender: (params: { kanaele?: KalenderKanal[]; limit?: number; offset?: number;
                       sendeAccountId?: number | null } = {}) =>
    api.get<KalenderResult>('/lead-gen/leads/kalender'
      + `?limit=${params.limit ?? 200}&offset=${params.offset ?? 0}`
      + (params.kanaele?.length ? `&kanaele=${params.kanaele.join(',')}` : '')
      + (params.sendeAccountId != null ? `&sende_account_id=${params.sendeAccountId}` : '')),

  // ── Mehrere Telefonnummern je Person/Zentrale (UMBAU.md Phase 4) ─────────────────────────────
  // Identität einheitlich: entweder personKey ODER central:true ODER linkedinUrl/name.
  phones: (id: number) =>
    api.get<{ phones: LeadPhone[] }>(`/lead-gen/leads/${id}/phones`),
  addPhone: (id: number, body: { personKey?: string | null; central?: boolean;
                                 linkedinUrl?: string | null; name?: string | null;
                                 phone: string; label?: string; source?: string; makePrimary?: boolean }) =>
    api.post<DomainLead & { existing?: boolean }>(`/lead-gen/leads/${id}/phones`, {
      person_key: body.personKey, central: body.central,
      linkedin_url: body.linkedinUrl, name: body.name,
      phone: body.phone, label: body.label, source: body.source, make_primary: body.makePrimary,
    }),
  updatePhone: (id: number, body: { personKey?: string | null; central?: boolean;
                                    linkedinUrl?: string | null; name?: string | null;
                                    phoneKey: string; phone?: string; label?: string; isPrimary?: boolean }) =>
    api.patch<DomainLead>(`/lead-gen/leads/${id}/phones`, {
      person_key: body.personKey, central: body.central,
      linkedin_url: body.linkedinUrl, name: body.name,
      phone_key: body.phoneKey, phone: body.phone, label: body.label, is_primary: body.isPrimary,
    }),
  removePhone: (id: number, body: { personKey?: string | null; central?: boolean;
                                    linkedinUrl?: string | null; name?: string | null; phoneKey: string }) =>
    api.post<DomainLead>(`/lead-gen/leads/${id}/phones/remove`, {
      person_key: body.personKey, central: body.central,
      linkedin_url: body.linkedinUrl, name: body.name, phone_key: body.phoneKey,
    }),
  setPhoneDefect: (id: number, body: { personKey?: string | null; central?: boolean;
                                       linkedinUrl?: string | null; name?: string | null;
                                       phoneKey: string; reason: PhoneDefectReason | null; note?: string }) =>
    api.post<{ lead: DomainLead; phone_key: string }>(`/lead-gen/leads/${id}/phones/defect`, {
      person_key: body.personKey, central: body.central,
      linkedin_url: body.linkedinUrl, name: body.name,
      phone_key: body.phoneKey, reason: body.reason, note: body.note,
    }),

  ingest: (replace = false, clientId?: number | null) =>
    api.post<{ inserted: number; updated: number; deleted: number; batch_id: string; client_id: number }>(
      '/lead-gen/ingest',
      { replace, ...(clientId != null ? { client_id: clientId } : {}) },
    ),

  enrich: (limit?: number, filter?: { countries?: string[]; sources?: string[] }) =>
    api.post<{ started: boolean; count: number; message?: string }>(
      '/lead-gen/enrich',
      {
        ...(limit != null ? { limit } : {}),
        ...(filter?.countries?.length ? { countries: filter.countries } : {}),
        ...(filter?.sources?.length ? { sources: filter.sources } : {}),
      },
    ),

  // Batch-extract the Impressum record for all qualified leads not yet checked (free).
  enrichImpressum: (limit?: number) =>
    api.post<{ started: boolean; count: number; message?: string }>(
      '/lead-gen/enrich-impressum',
      limit != null ? { limit } : {},
    ),

  // Funnel-Panel Stufe 7: paid LinkedIn contact search (Apify) over the server-selected backlog
  // (leads whose free Impressum pass found nothing) — progress via SSE (leadgen_contacts_*).
  contactsRun: (limit?: number) =>
    api.post<{ started: boolean; count: number; message?: string }>(
      '/lead-gen/leads/contacts-run',
      limit != null ? { limit } : {},
    ),

  // Trigger the daily Meta Ad Library source (free, signal-first, capped). Progress via SSE
  // (leadgen_meta_source_*). No-op server-side when META_AD_LIBRARY_TOKEN is unset.
  sourceMeta: (body?: { terms_per_run?: number; daily_new_max?: number }) =>
    api.post<{ started: boolean; message?: string }>('/lead-gen/source-meta', body ?? {}),

  // Admin: run the daily reservation (idempotent top-up). Broadcasts so all clients refetch live.
  reserveDaily: () =>
    api.post<{ date: string; counts: Record<string, number> }>('/lead-gen/reserve-daily', {}),

  // ⚡ Flow-HUD: FR-Budget des aufrufenden Users (heute/Woche + Caps). Der Flow blockt Neu-Karten,
  // sobald ein Budget erschöpft ist; die Leiste färbt sich Richtung Cap.
  // accountId = gewählter Sende-Account → Zähler UND Limit genau dieses Accounts (gleiche Rechnung
  // wie die 429-Sperre). Ohne: konservative Sicht über alle Accounts des Inhabers.
  getFrBudget: (ownerId?: number | null, accountId?: number | null) => {
    const q = new URLSearchParams();
    if (ownerId != null) q.set('owner_id', String(ownerId));
    if (accountId != null) q.set('account_id', String(accountId));
    const s = q.toString();
    return api.get<{ today: number; week: number; daily_limit: number; weekly_limit: number }>(
      `/lead-gen/fr-budget${s ? `?${s}` : ''}`);
  },

  // Geteiltes fraud0-Freigabe-Tageskontingent (X/50 heute) fürs Reviewer-„Neu". Read-only; der harte
  // Stopp lebt clientseitig (LeadStack). `today` = echte Reviewer-Freigaben seit Berlin-Mitternacht.
  getReviewBudget: () =>
    api.get<{ today: number; daily_limit: number; remaining: number }>('/lead-gen/review-budget'),

  // Admin: distribution rules for the daily reservation (per-user counts + exclusion toggles).
  getDistributionConfig: () =>
    api.get<DistributionConfig>('/lead-gen/distribution-config'),
  // Saving re-runs the daily reservation immediately (server-side) so the new rules apply at once.
  setDistributionConfig: (body: DistributionConfig) =>
    api.put<{ ok: boolean; config: DistributionConfig; reservation: { date: string; counts: Record<string, number> } }>(
      '/lead-gen/distribution-config', body),

  // Die eigenen Märkte (welche Länder im Stapel sichtbar sind). Eigene Adresse aus demselben Grund
  // wie die Wiedervorlage-Vorschläge unten: distribution-config ist admin-only, der Stapel rendert
  // aber für jede Rolle. Bekäme ein Setter hier ein 403, fiele die Oberfläche still auf DACH zurück
  // — „für mich gibt es keine UK-Leads" wäre dann von „UK ist nicht freigeschaltet" nicht zu
  // unterscheiden. Deshalb wird ein Fehler hier auch NICHT verschluckt.
  getMyMarkets: () =>
    api.get<{ markets: string[]; countries: string[]; labels: Record<string, string> }>(
      '/lead-gen/my-markets'),

  // Wiedervorlage-Vorschläge fürs Anruf-Cockpit. Eigene Adresse statt distribution-config, weil
  // letztere admin-only ist — anrufen können auch Setter. POST hängt AN (ersetzt nicht) und fährt
  // bewusst KEINE Tageszuteilung neu, anders als setDistributionConfig.
  getCallbackPresets: () =>
    api.get<{ presets: CallbackPreset[] }>('/lead-gen/callback-presets'),
  addCallbackPreset: (body: Omit<CallbackPreset, 'default'>) =>
    api.post<{ presets: CallbackPreset[] }>('/lead-gen/callback-presets', body),

  // Global write-list gate (economics floors + assumptions). Saving re-runs the daily reservation so the
  // new floors apply to the setter stacks + reservation at once.
  getWritelistGate: () =>
    api.get<WritelistGate>('/lead-gen/writelist-gate'),
  setWritelistGate: (body: WritelistGate) =>
    api.put<{ ok: boolean; gate: WritelistGate; reservation: { date: string; counts: Record<string, number> } }>(
      '/lead-gen/writelist-gate', body),

  // Scrape real SimilarWeb visits for the CURRENT filtered set (same filters as listLeads).
  // Runs in the background in 10er batches; progress arrives via SSE (leadgen_similarweb_*).
  scrapeSimilarWeb: (f: Partial<LeadGenFilters>) =>
    api.post<{ started: boolean; count: number; est_cost_usd?: number; running?: boolean; message?: string }>(
      '/lead-gen/scrape-similarweb',
      f,
    ),

  // Scrape-planner PREVIEW (never scrapes): cost + expected leads + confidence for a chosen range/band.
  scrapePreview: (p: ScrapePreviewParams, signal?: AbortSignal) => {
    const qs = new URLSearchParams();
    const set = (k: string, v: unknown) => { if (v != null && v !== false && v !== '') qs.set(k, String(v)); };
    set('runs_google_ads', p.runs_google_ads);
    set('source', p.source);
    set('site_kind', p.site_kind);
    set('business_category', p.business_category);
    set('crux_present', p.crux_present);
    set('max_crux_magnitude', p.max_crux_magnitude);
    set('min_crux_magnitude', p.min_crux_magnitude);
    set('min_rank', p.min_rank);
    set('max_rank', p.max_rank);
    set('min_similarweb_visits', p.min_similarweb_visits);
    set('max_similarweb_visits', p.max_similarweb_visits);
    if (p.exclude_subdomains) qs.set('exclude_subdomains', 'true');
    if (p.exclude_foreign_cctld) qs.set('exclude_foreign_cctld', 'true');
    if (p.exclude_small_market_crux) qs.set('exclude_small_market_crux', 'true');
    set('band_min', p.band_min);
    set('band_max', p.band_max);
    const q = qs.toString();
    return api.get<ScrapePreview>(`/lead-gen/scrape-preview${q ? `?${q}` : ''}`, { signal });
  },

  // Funnel-Panel: alle sechs Stufen in EINER Anfrage. Streng lesend, loest keinen Lauf aus.
  // Nimmt dieselben Filter wie die Liste — nur die Sessions-Stufe wertet sie aus.
  funnelPlan: (p: ScrapePreviewParams & { markets?: string | null; status?: string | null },
               signal?: AbortSignal) => {
    const qs = new URLSearchParams();
    const set = (k: string, v: unknown) => { if (v != null && v !== false && v !== '') qs.set(k, String(v)); };
    set('runs_google_ads', p.runs_google_ads);
    set('source', p.source);
    set('site_kind', p.site_kind);
    set('business_category', p.business_category);
    set('crux_present', p.crux_present);
    set('max_crux_magnitude', p.max_crux_magnitude);
    set('min_crux_magnitude', p.min_crux_magnitude);
    set('min_rank', p.min_rank);
    set('max_rank', p.max_rank);
    set('min_similarweb_visits', p.min_similarweb_visits);
    set('max_similarweb_visits', p.max_similarweb_visits);
    set('markets', p.markets);
    if (p.exclude_subdomains) qs.set('exclude_subdomains', 'true');
    if (p.exclude_foreign_cctld) qs.set('exclude_foreign_cctld', 'true');
    if (p.exclude_small_market_crux) qs.set('exclude_small_market_crux', 'true');
    const q = qs.toString();
    return api.get<FunnelPlan>(`/lead-gen/funnel-plan${q ? `?${q}` : ''}`, { signal });
  },

  // Rückwärts-Rechner: Ziel "kontaktierbare Leads/Tag" rein, nötige Menge + Kosten je Stufe raus.
  // Streng lesend, dieselben Filter wie funnelPlan (nur Stufe „Sessions" wertet sie aus).
  funnelReversePlan: (ziel: number,
                      p: ScrapePreviewParams & { markets?: string | null; status?: string | null },
                      signal?: AbortSignal, modus: ZielModus = 'taeglich') => {
    const qs = new URLSearchParams();
    const set = (k: string, v: unknown) => { if (v != null && v !== false && v !== '') qs.set(k, String(v)); };
    qs.set('ziel', String(ziel));
    qs.set('modus', modus);
    set('runs_google_ads', p.runs_google_ads);
    set('source', p.source);
    set('site_kind', p.site_kind);
    set('business_category', p.business_category);
    set('crux_present', p.crux_present);
    set('max_crux_magnitude', p.max_crux_magnitude);
    set('min_crux_magnitude', p.min_crux_magnitude);
    set('min_rank', p.min_rank);
    set('max_rank', p.max_rank);
    set('min_similarweb_visits', p.min_similarweb_visits);
    set('max_similarweb_visits', p.max_similarweb_visits);
    set('markets', p.markets);
    if (p.exclude_subdomains) qs.set('exclude_subdomains', 'true');
    if (p.exclude_foreign_cctld) qs.set('exclude_foreign_cctld', 'true');
    if (p.exclude_small_market_crux) qs.set('exclude_small_market_crux', 'true');
    const q = qs.toString();
    return api.get<ReverseFunnelPlan>(`/lead-gen/funnel-reverse${q ? `?${q}` : ''}`, { signal });
  },

  // Persist the global ICP qualify band + re-classify all measured leads to it (admin, explicit Save).
  saveIcpBand: (qualify_visits_min: number, qualify_visits_max: number) =>
    api.post<{ band: IcpBand; reclassified: { qualified: number; disqualified: number } }>(
      '/lead-gen/icp-band',
      { qualify_visits_min, qualify_visits_max },
    ),

  // TIER 2: fetch the full radeance marketing-mix for already-qualified leads missing it.
  // Progress via SSE (leadgen_similarweb_mix_*). prioritizeAdvertisers (default true) sorts likely
  // advertisers (free ICP signal) first in the capped batch; every in-band qualified lead stays
  // eligible either way · the signal is a priority, not a filter (Fix Nr. 08).
  scrapeSimilarWebMix: (prioritizeAdvertisers = true) =>
    api.post<{ started: boolean; count: number; est_cost_usd?: number; running?: boolean; message?: string }>(
      '/lead-gen/scrape-similarweb-mix',
      { prioritize_advertisers: prioritizeAdvertisers },
    ),

  getTargetRoles: () => api.get<TargetRoles>('/lead-gen/target-roles'),

  // Editable people-finder priority list (the ranking logic, admin-editable).
  getRolePriority: () =>
    api.get<{ priority: RolePriorityEntry[]; defaults: RolePriorityEntry[] }>('/lead-gen/role-priority'),
  saveRolePriority: (priority: RolePriorityEntry[]) =>
    api.put<{ priority: RolePriorityEntry[] }>('/lead-gen/role-priority', { priority }),
  resetRolePriority: () =>
    api.put<{ priority: RolePriorityEntry[] }>('/lead-gen/role-priority', { reset: true }),

  // Editable swipe-stack category priority (order = priority; the LinkedIn-contact-found band first by default).
  getCategoryPriority: () =>
    api.get<{ priority: CategoryPriorityEntry[]; catalog: CategoryCatalogItem[]; defaults: CategoryPriorityEntry[] }>(
      '/lead-gen/category-priority'),
  saveCategoryPriority: (priority: CategoryPriorityEntry[]) =>
    api.put<{ priority: CategoryPriorityEntry[] }>('/lead-gen/category-priority', { priority }),
  resetCategoryPriority: () =>
    api.put<{ priority: CategoryPriorityEntry[] }>('/lead-gen/category-priority', { reset: true }),

  findContacts: (
    id: number,
    body: { titles?: string[]; seniorities?: string[]; limit?: number; provider?: 'serper' | 'apify' },
  ) => api.post<FindContactsResult>(`/lead-gen/leads/${id}/find-contacts`, body),

  // Angebots-Historie (FR-Events) eines Leads, chronologisch (älteste zuerst).
  frEvents: (id: number) =>
    api.get<{ events: FrEvent[] }>(`/lead-gen/leads/${id}/fr-events`),

  // Team-Kommentare eines Leads (Kommentar-Sektion), chronologisch mit Autor + Zeit.
  // personKey: die offene Personen-Karte. Gesetzt → Firmen-Notizen + die dieser Person; ohne →
  // alles (Firmen-Karte, z.B. in „Neu").
  getLeadComments: (id: number, personKey?: string | null) =>
    api.get<{ comments: LeadComment[] }>(
      `/lead-gen/leads/${id}/comments${personKey ? `?person_key=${encodeURIComponent(personKey)}` : ''}`),
  // Team-weite eigene Pitch-Vorlagen (Titel + Body mit {{Bausteinen}}). getPitchTemplates lädt alle,
  // savePitchTemplates ersetzt die ganze Liste (Singleton-Config server-seitig).
  getPitchTemplates: () =>
    api.get<{ templates: PitchTemplate[] }>(`/lead-gen/pitch-templates`),
  savePitchTemplates: (templates: PitchTemplate[]) =>
    api.put<{ templates: PitchTemplate[] }>(`/lead-gen/pitch-templates`, { templates }),
  // Vorlagen der Kollegen (gruppiert nach Besitzer) zum Übernehmen ins eigene Set.
  getTeamPitchTemplates: () =>
    api.get<{ groups: PitchTemplateGroup[] }>(`/lead-gen/pitch-templates/team`),
  // Overrides der EINGEBAUTEN Vorlagen (Standard/SEA) pro User · {id: body mit {Platzhaltern}}.
  getPitchBuiltins: () =>
    api.get<{ overrides: Record<string, string> }>(`/lead-gen/pitch-builtins`),
  savePitchBuiltins: (overrides: Record<string, string>) =>
    api.put<{ overrides: Record<string, string> }>(`/lead-gen/pitch-builtins`, { overrides }),

  // ── MAIL-Vorlagen: eigener Namensraum (2026-07-30) ─────────────────────────
  // Absichtlich NICHT dieselben Vorlagen wie beim Pitch: eine Mail-Vorlage trägt einen BETREFF, und
  // ein LinkedIn-Kurztext ist keine Mail. Gleiche Editor-Komponenten, getrennte Daten.
  getEmailTemplates: () =>
    api.get<{ templates: PitchTemplate[] }>(`/lead-gen/email-templates`),
  saveEmailTemplates: (templates: PitchTemplate[]) =>
    api.put<{ templates: PitchTemplate[] }>(`/lead-gen/email-templates`, { templates }),
  getTeamEmailTemplates: () =>
    api.get<{ groups: PitchTemplateGroup[] }>(`/lead-gen/email-templates/team`),
  // Overrides der FESTEN Mail-Vorlagen (Kalt/Hotline). Wert ist ein OBJEKT {subject, body} · bei den
  // Pitch-Builtins ist es ein reiner String, weil dort kein Betreff existiert.
  getEmailBuiltins: () =>
    api.get<{ overrides: Record<string, EmailBuiltinOverride> }>(`/lead-gen/email-builtins`),
  saveEmailBuiltins: (overrides: Record<string, EmailBuiltinOverride>) =>
    api.put<{ overrides: Record<string, EmailBuiltinOverride> }>(`/lead-gen/email-builtins`, { overrides }),

  // Globale Sortier-Schalter (Nummern zuerst / Search-Spend zuerst) · team-weit, bleiben an bis ausgeschaltet.
  getSortPrefs: () =>
    api.get<{ german_phone_first: boolean; search_spend_first: boolean }>(`/lead-gen/sort-prefs`),
  saveSortPrefs: (prefs: { german_phone_first: boolean; search_spend_first: boolean }) =>
    api.put<{ german_phone_first: boolean; search_spend_first: boolean }>(`/lead-gen/sort-prefs`, prefs),

  // person: der Bezug der offenen Karte. Weggelassen → der Kommentar gilt der ganzen Firma.
  // Bei einer Antwort (parentId) erbt der Server den Bezug vom Eltern-Kommentar und ignoriert das hier.
  addLeadComment: (id: number, body: string, parentId?: number | null,
                   person?: { key: string | null; name?: string | null } | null) =>
    api.post<LeadComment>(`/lead-gen/leads/${id}/comment`, {
      body, parent_id: parentId ?? null,
      person_key: person?.key ?? null, person_name: person?.name ?? null,
    }),

  // Eigenen Kommentar bearbeiten / loeschen (Server prueft die Autorschaft; Admin darf jeden loeschen).
  editLeadComment: (leadId: number, commentId: number, body: string) =>
    api.patch<LeadComment>(`/lead-gen/leads/${leadId}/comment/${commentId}`, { body }),
  deleteLeadComment: (leadId: number, commentId: number) =>
    api.delete<{ ok: boolean; ids: number[] }>(`/lead-gen/leads/${leadId}/comment/${commentId}`),

  // Bulk people-search over the selected leads (runs sequentially server-side; already-searched
  // leads are skipped). Canary = send a small id slice first, then the rest after the checkpoint.
  bulkFindContacts: (
    ids: number[],
    body: { provider?: 'serper' | 'apify'; limit?: number } = {},
  ) => api.post<BulkFindContactsResult>('/lead-gen/leads/bulk-find-contacts', { ids, ...body }),

  // Reichert die schon gefundenen people_json-Kontakte um Mobilnummern an (bezahlt, pay-on-success:
  // $0.04/Nummer, $0 bei 0 Treffern). Erst nach HoS-Freigabe erlaubt (sonst 403 not_approved).
  enrichPhones: (id: number) =>
    api.post<EnrichPhonesResult>(`/lead-gen/leads/${id}/enrich-phones`, {}),

  // Bulk-Nummern-Anreicherung über die ausgewählten Leads.
  bulkEnrichPhones: (ids: number[]) =>
    api.post<{ processed: number; updated_total: number; found_total: number;
               cost_usd_total: number; per_lead: { lead_id: number; ok: boolean;
               code?: string; updated?: number; found?: number }[] }>(
      '/lead-gen/leads/bulk-enrich-phones', { ids }),

  // Sieht bei Lusha nach, WAS zu den Kontakten hinterlegt ist — deckt NICHTS auf. Kostet 1 Credit je
  // angefangene 25 Kontakte (revealPhone kostet 5 Credits je Nummer, das macht der Setter selbst).
  // Ein Lead mit 2 Personen kostet einzeln so viel wie 25 → für die Fläche bulkLushaCheck nehmen.
  lushaCheck: (id: number) =>
    api.post<LushaCheckResult>(`/lead-gen/leads/${id}/lusha-check`, {}),

  // Lusha-Verfügbarkeit über mehrere Leads, gebündelt zu 100er-Anfragen (deutlich billiger als
  // Lead für Lead, weil je angefangene 25 Kontakte abgerechnet wird).
  bulkLushaCheck: (ids: number[]) =>
    api.post<{ processed: number; updated_total: number; checked_total: number;
               with_phone_total: number; credits_used: number;
               per_lead: { lead_id: number; ok: boolean; code?: string; message?: string;
                           updated?: number; checked?: number; with_phone?: number }[] }>(
      '/lead-gen/leads/bulk-lusha-check', { ids }),

  // Deckt die Nummer EINER Person auf (revealPhone, ~5 Credits) — die teure Hälfte. Bewusst EIN
  // Klick, EINE Person, kein Bulk-Aufdecken. `expectedCredits` muss mit dem Preis aus dem letzten
  // Prüflauf übereinstimmen (row.lusha_reveal_credits) — weicht er ab, antwortet der Server mit
  // 409 statt aufzudecken ("nie aufdecken, ohne die Kosten vorher zu kennen").
  lushaReveal: (leadId: number, personKey: string, expectedCredits: number) =>
    api.post<{ lead_id: number; person_key: string; phone: string; non_de_phone: boolean;
               credits_used: number }>(
      `/lead-gen/leads/${leadId}/lusha-reveal`,
      { person_key: personKey, expected_credits: expectedCredits }),

  // Extract the Impressum record (free HTTP fetch + regex/Haiku). force=true bypasses the TTL guard.
  extractImpressum: (id: number, force = false) =>
    api.post<ExtractImpressumResult>(`/lead-gen/leads/${id}/extract-impressum`, { force }),

  // Setter flags the extracted Impressum company/GF as WRONG (free). Suppresses the GF display + logs
  // it for the accuracy metric. verdict ∈ wrong_company | wrong_person | wrong_both.
  impressumFeedback: (id: number, verdict?: string, note?: string) =>
    api.post<{ ok: boolean; stats: Record<string, unknown> }>(
      `/lead-gen/leads/${id}/impressum-feedback`, { verdict, note }),

  // Scrape GF names from NorthData via Apify (~$0.006/company, PAID). force=true bypasses the TTL guard.
  scrapeNorthdata: (id: number, force = false) =>
    api.post<ScrapeNorthdataResult>(`/lead-gen/leads/${id}/scrape-northdata`, { force }),

  // Setter marks a lead as "no LinkedIn presence found" → leaves the missing_linkedin queue.
  markUnresolvable: (id: number) =>
    api.post<DomainLead>(`/lead-gen/leads/${id}/mark-unresolvable`, {}),

  // Subsidiary → parent/holding override (read off NorthData). Retargets LinkedIn company + people
  // search onto the parent. Optional gf_names merge into the Kontakt list. Free + idempotent.
  setParentCompany: (id: number, body: { parent_company_name: string; gf_names?: string[] }) =>
    api.post<DomainLead>(`/lead-gen/leads/${id}/parent-company`, body),

  // Build a Business-Outreach prefill payload from a lead (does NOT create the target).
  // hints: was gerade auf der Karte steht (gewählter Kontakt, umgestellte Sprache). Ohne sie nimmt
  // der Server die Werte vom Lead — die Karte darf präzisieren, nicht ersetzen.
  toOutreach: (id: number, hints: {
    contact_name?: string; contact_linkedin_url?: string; contact_position?: string; lang?: string;
  } = {}) =>
    api.post<LeadToOutreachResult>(`/lead-gen/leads/${id}/to-outreach`, hints),

  // Head-of-Sales sign-off toggle (admin or head_of_sales). at/by stamped server-side.
  hosApprove: (id: number, approved: boolean) =>
    api.post<DomainLead>(`/lead-gen/leads/${id}/hos-approve`, { approved }),

  // Head-of-Sales tri-state decision: 'approve' | 'reject' | 'reset' (offen). at/by stamped server-side.
  hosDecision: (id: number, decision: 'approve' | 'reject' | 'reset') =>
    api.post<DomainLead>(`/lead-gen/leads/${id}/hos-decision`, { decision }),

  // "Handtuch drauflegen"/abnehmen: claimed=true → freigeben + für fraud0 beanspruchen; false → abnehmen
  // + Entscheidung zurücksetzen (Lead zurück in 'offen'). at/by stamped server-side.
  fraud0Claim: (id: number, claimed: boolean) =>
    api.post<DomainLead>(`/lead-gen/leads/${id}/fraud0-claim`, { claimed }),

  // „Nicht anrufen": EINE Firmensperre für alle Kanäle (Telefon, Anfrage, Anschreiben, Nachfassen,
  // Mail — und ausserhalb von fraud0 auch Business-Outreach und Sales-CRM). Der Grund ist beim
  // Setzen Pflicht. Aufheben (on=false) darf nur, wer gesetzt hat, oder ein Admin → sonst 403.
  // Die Antwort trägt block_reason bereits nach, weil ein Nachladen über getLead es verlöre.
  setDoNotContact: (id: number, on: boolean, reason?: string) =>
    api.post<{ lead: DomainLead; result: unknown }>(
      `/lead-gen/leads/${id}/do-not-contact`, { on, reason }),

  // „Person sperren": Sperre für GENAU EINE Person (alle ihre Nummern), nur der Anrufliste
  // (call_table) — mail_table bleibt unberührt. Grund ist beim Setzen Pflicht. Aufheben (on=false)
  // darf nur, wer gesetzt hat, oder ein Admin → sonst 403.
  setDoNotCallPerson: (leadId: number, personKey: string, on: boolean, reason?: string) =>
    api.post<{ result: unknown; entry: DoNotCallPersonEntry | null }>(
      `/lead-gen/leads/${leadId}/people/do-not-call`, { person_key: personKey, on, reason }),

  // Gesperrte Personen einer Firma — für die Aufheben-Ansicht im Details-Overlay.
  listDoNotCallPerson: (leadId: number) =>
    api.get<{ entries: DoNotCallPersonEntry[] }>(`/lead-gen/leads/${leadId}/do-not-call-person`),

  // Welle 2, L7 (2026-08-26): "Gewonnen" — Kurzformular fuer die gewonnene Adresse/den
  // Kontaktweg, landet an der Person; setzt den Lead auf status='won'.
  gewonnen: (id: number, personKey: string, note: string, kanal?: string) =>
    api.post<{ lead: DomainLead }>(
      `/lead-gen/leads/${id}/gewonnen`, { person_key: personKey, note, kanal }),

  // Welle 2, L7: "Keine Lust mehr" — firmenweite Wiedervorlage in 90 Tagen (status='lost' +
  // cooldown_until), KEIN do_not_contact. Grund ist optional.
  keineLustMehr: (id: number, reason?: string) =>
    api.post<{ lead: DomainLead }>(
      `/lead-gen/leads/${id}/keine-lust-mehr`, { reason }),

  // leitkraft Send-Pult (admin): übergebbarer Pool (qualified · ohne gefundenen Kontakt · noch nicht
  // fraud0-claimed) nach Firmen-Größenband gebucketed.
  getSendableSummary: () =>
    api.get<SendableSummary>('/lead-gen/fraud0/sendable-summary'),
  // Übergibt eine gewählte Anzahl Leads PRO Größenband an fraud0 (Bulk-Claim → Handtuch). Manuell.
  sendToFraud0: (quotas: Record<string, number>) =>
    api.post<{ claimed: number; summary: SendableSummary }>('/lead-gen/fraud0/send', { quotas }),
  // Übergibt ALLE "gesucht aber leer"-Leads (bezahlte Suche ohne Treffer) wholesale an fraud0.
  sendSearchedEmptyToFraud0: () =>
    api.post<{ claimed: number; summary: SendableSummary }>('/lead-gen/fraud0/send', { searched_empty: true }),
};

/** One company-size band in the send-panel: how many handable leads sit in it right now. */
export interface SendableBand {
  key: string;
  label: string;
  min: number | null;
  max: number | null;
  available: number;
}
/** The handable pool (qualified · no found contact · not yet fraud0-claimed). Size bands cover only the
 *  never-searched leads; `searched_empty` is the separate "paid search ran but found nobody" group. */
export interface SendableSummary {
  total: number;
  bands: SendableBand[];
  searched_empty: { available: number; paid: number };
}

/** Prefill payload for handing a Fraud0 lead to Business Outreach (review-form UX). */
export interface OutreachPrefill {
  company_name: string | null;
  industry: string | null;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  website_url: string | null;
  resolved_domain: string | null;
  linkedin_url: string | null;        // company LinkedIn
  linkedin_gf_url: string | null;     // GF LinkedIn
  pain_point: string | null;
  language: string;
  platforms: string[];
  marketing_contact_name: string | null;
  marketing_linkedin_url: string | null;
  marketing_contact_status: string | null;
  marketing_position: string | null;
  fraud0_lead_id: number;
  fraud0_pitch_json: string;
  deal_value: number | null;
  client_id: number | null;
}

export interface LeadToOutreachResult {
  prefill: OutreachPrefill;
  pitch_brief: { facts: Array<{ rank: number; key: string; type: string; text: string }>;
    company_name: string | null; recoverable_eur: number | null };
  marketing_contact_found: boolean;
  existing_target: { id: number; company_name: string } | null;
}

// Fraud0 phone-only lead → Sales CRM handoff (mirror of the outreach bridge, for the call pipeline).
export interface ScriptContext {
  contact_name: string | null;
  job_title: string | null;
  company: string | null;
  paid_share: number | null;    // 0..1
  display_share: number | null; // 0..1
  recoverable_eur: number | null; // rückholbarer Betrag/Jahr (roh; wird beim Anzeigen abgerundet)
}
export interface SalesCrmPrefill {
  name: string;
  company: string;
  source: string;
  source_url: string | null;
  website: string | null;
  email: string | null;
  phone: string | null;
  niche: string | null;
  language: string;
  follow_up_date: string | null;
  fraud0_lead_id: number;
  client_id: number | null;
  // Auto-Fill fürs Call-Skript (Jobtitel + Traffic-Anteile + kompletter Kontext).
  job_title: string | null;
  paid_share: number | null;
  display_share: number | null;
  script_context: ScriptContext;
}
export interface LeadToSalesCrmResult {
  prefill: SalesCrmPrefill;
  existing_lead: { id: number; company: string } | null;
}

// Call-disposition values for the Telefon stack (mirror of CALL_STATUSES in db_web/lead_gen.py).
// `callback` = bei diesem Ergebnis ist ein Rückruf die Absicht → die Zeitwahl auf der Karte hat
//   den Standard-Vorschlag VORGEWÄHLT. Sichtbar ist sie seit 2026-08-10 bei jedem Ergebnis ausser
//   den Absagen (`negative`) — „Nicht erreicht" hatte sonst gar keine Wiedervorlage. Ohne Vorwahl
//   stempelt der Server nach der Runden-Liste (call_cadence_days, db_web/lead_gen.py).
// `negative` = Absage → keine Zeitwahl; Speichern räumt ein vorhandenes callback_at ab.
// `mail` = Hotline/Zentrale will eine Mail → blendet den Mail-Composer auf der Karte ein.
// `kurz` = dieselbe Auskunft als Satzteil, für Stellen, an denen das Ergebnis MITLÄUFT statt
//   gewählt zu werden („Tom (Marketing) vor 2 Std. angerufen · niemand rangegangen", Telefon-
//   Tabelle). Das Etikett aus der Auswahlliste passt dort nicht: „Nicht erreicht" ist als Knopf
//   richtig und als Anhang an einen Satz falsch. Beides steht hier, damit eine neue Disposition
//   nicht an zwei Orten nachgetragen werden muss — fehlt `kurz`, fällt die Anzeige auf `label`
//   zurück (siehe `dispositionKurz`).
// `gesprochen` = bei diesem Ergebnis hat im Betrieb tatsächlich jemand abgehoben. Danach richtet
//   sich, wie laut die Auskunft im Haus steht: „schon gesprochen" ändert die eigene Eröffnung,
//   „niemand rangegangen" ist eine Randnotiz. Bewusst je Eintrag gesetzt und nicht aus `negative`
//   oder `callback` abgeleitet — die beiden beantworten andere Fragen, und die Zuordnung fällt
//   an zwei Stellen auseinander: `sekretariat` ist ein Gespräch (mit dem Vorzimmer), und
//   `falsche_nummer` ist keins mit DIESEM Betrieb, obwohl jemand abgehoben hat.
export const CALL_DISPOSITIONS: { value: string; label: string; kurz?: string; gesprochen?: boolean;
                                  callback?: boolean; negative?: boolean; mail?: boolean }[] = [
  { value: 'nicht_erreicht', label: 'Nicht erreicht', kurz: 'niemand rangegangen' },
  { value: 'sekretariat', label: 'Sekretariat', kurz: 'nur Sekretariat', gesprochen: true, callback: true },
  // Mail-Fall braucht ebenfalls eine Wiedervorlage: ohne sie verschwindet der Lead nach dem
  // Verschicken aus dem Blick, statt nachgefasst zu werden.
  { value: 'mail_gewuenscht', label: 'Mail gewünscht', kurz: 'will eine Mail', gesprochen: true, callback: true, mail: true },
  { value: 'rueckruf', label: 'Ruft zurück', kurz: 'ruft zurück', gesprochen: true, callback: true },
  { value: 'wiedervorlage', label: 'Wiedervorlage', kurz: 'Wiedervorlage vereinbart', gesprochen: true, callback: true },
  { value: 'erreicht', label: 'Erreicht', kurz: 'gesprochen', gesprochen: true },
  { value: 'interessiert', label: 'Interessiert', kurz: 'interessiert', gesprochen: true },
  { value: 'kein_interesse', label: 'Kein Interesse', kurz: 'kein Interesse', gesprochen: true, negative: true },
  { value: 'falsche_nummer', label: 'Falsche Nummer', kurz: 'falsche Nummer', negative: true },
  { value: 'nicht_zustaendig', label: 'Nicht zuständig', kurz: 'nicht zuständig', gesprochen: true },
];

// Das Ergebnis eines Anrufs als Satzteil — samt der Frage, ob dabei jemand am Apparat war.
// `null` für einen unbekannten Wert: lieber gar keine Auskunft als eine erfundene (dieselbe Regel
// wie beim Namen der Person, siehe `person_unknown`).
export function dispositionKurz(value: string | null | undefined):
    { text: string; gesprochen: boolean } | null {
  if (!value) return null;
  const d = CALL_DISPOSITIONS.find((x) => x.value === value);
  if (!d) return null;
  return { text: d.kurz ?? d.label.toLowerCase(), gesprochen: !!d.gesprochen };
}
